/*
 * Private product inquiry frame for main-product variants at the configured
 * high-value threshold. Kept route-scoped so quick buy and other product forms
 * retain their existing behavior.
 */

const InquiryAnalytics = (() => {
const DESTINATION = "G-3BE5NK5G0V";
const TTL = 15 * 60 * 1000;
const allowed = () => window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true;

class InquiryAnalytics {
  constructor(host) {
    this.host = host;
    this.key = `rr:inquiry:analytics:${host.form?.id || ""}`;
    this.receipt = null;
    this.timer = null;
    this.disposed = false;
    this.onConsent = () => {
      if (!allowed()) this.clear();
    };
    document.addEventListener("visitorConsentCollected", this.onConsent);
    try {
      const stored = JSON.parse(window.sessionStorage.getItem(this.key) || "null");
      if (allowed() && stored?.path === location.pathname && stored.expires > Date.now() &&
          /^[0-9a-f-]{36}$/.test(stored.id) &&
          ["product", "concierge", "general"].includes(stored.type)) this.receipt = stored;
      else window.sessionStorage.removeItem(this.key);
    } catch {}
  }

  save() {
    try { window.sessionStorage.setItem(this.key, JSON.stringify(this.receipt)); } catch {}
  }

  clear() {
    clearTimeout(this.timer);
    this.timer = null;
    this.receipt = null;
    try { window.sessionStorage.removeItem(this.key); } catch {}
  }

  prepare() {
    try {
      if (!allowed()) { this.clear(); return; }
      clearTimeout(this.timer);
      const product = this.host.productTransport?.dataset || {};
      this.receipt = {
        id: this.host.submissionLeadId || window.crypto.randomUUID(),
        path: location.pathname,
        expires: Date.now() + TTL,
        type: this.host.localName === "rr-product-inquiry" ? "product" :
          location.pathname === "/pages/concierge" ? "concierge" : "general",
        sku: (product.productSku || "").slice(0, 100),
        product: (product.productName || "").slice(0, 100),
        confirmed: false,
        sent: false
      };
      this.save();
    } catch { this.clear(); }
  }

  complete() {
    try {
      if (!allowed()) { this.clear(); return; }
      if (!this.receipt || this.receipt.sent || this.receipt.expires <= Date.now()) return;
      this.receipt.confirmed = true;
      this.save();
      this.send();
    } catch {}
  }

  send(attempt = 0) {
    if (this.disposed || !this.host.isConnected || !this.receipt?.confirmed || this.receipt.sent) return;
    if (!allowed()) { this.clear(); return; }
    if (typeof window.gtag !== "function") {
      if (attempt < 10) this.timer = setTimeout(() => this.send(attempt + 1), 500);
      return;
    }
    const receipt = this.receipt;
    const params = {
      send_to: DESTINATION,
      lead_id: receipt.id,
      inquiry_type: receipt.type,
      page_location: location.origin + receipt.path,
      ...(receipt.sku ? { item_id: receipt.sku } : {}),
      ...(receipt.product ? { item_name: receipt.product } : {})
    };
    if (window.Shopify?.theme?.role === "unpublished" || window.Shopify?.designMode) params.debug_mode = true;
    receipt.sent = true;
    this.save();
    try { window.gtag("event", "generate_lead", params); }
    catch { receipt.sent = false; this.save(); }
  }

  dispose() {
    this.disposed = true;
    clearTimeout(this.timer);
    document.removeEventListener("visitorConsentCollected", this.onConsent);
  }
}

return InquiryAnalytics;
})();

const PHONE_PATTERNS = {
  1: { mask: "(###) ###-####", placeholder: "(000) 000-0000" },
  33: { mask: "# ## ## ## ##", placeholder: "0 00 00 00 00" },
  39: { mask: "### ### ####", placeholder: "000 000 0000" },
  41: { mask: "## ### ## ##", placeholder: "00 000 00 00" },
  44: { mask: "#### ### ####", placeholder: "0000 000 0000" },
  49: { mask: "### ########", placeholder: "000 00000000" },
  61: { mask: "## #### ####", placeholder: "00 0000 0000" },
  64: { mask: "## ### ####", placeholder: "00 000 0000" },
  65: { mask: "#### ####", placeholder: "0000 0000" },
  81: { mask: "##-####-####", placeholder: "00-0000-0000" },
  82: { mask: "##-####-####", placeholder: "00-0000-0000" },
  91: { mask: "##### #####", placeholder: "00000 00000" },
  502: { mask: "#### ####", placeholder: "0000 0000" },
  852: { mask: "#### ####", placeholder: "0000 0000" },
  971: { mask: "## ### ####", placeholder: "00 000 0000" }
};

// Pinned from libphonenumber-js 1.13.10 examples.mobile.json so the adjacent
// country-code field can derive each region's national-number structure. The
// example digits remain internal and are replaced before a placeholder is shown.
const PHONE_EXAMPLE_NUMBERS = {
  AC: "40123",
  AD: "312345",
  AE: "501234567",
  AF: "701234567",
  AG: "2684641234",
  AI: "2642351234",
  AL: "672123456",
  AM: "77123456",
  AO: "923123456",
  AR: "91123456789",
  AS: "6847331234",
  AT: "664123456",
  AU: "412345678",
  AW: "5601234",
  AX: "412345678",
  AZ: "401234567",
  BA: "61123456",
  BB: "2462501234",
  BD: "1812345678",
  BE: "450001234",
  BF: "70123456",
  BG: "43012345",
  BH: "36001234",
  BI: "79561234",
  BJ: "0195123456",
  BL: "690001234",
  BM: "4413701234",
  BN: "7123456",
  BO: "71234567",
  BQ: "3181234",
  BR: "11961234567",
  BS: "2423591234",
  BT: "17123456",
  BW: "71123456",
  BY: "294911911",
  BZ: "6221234",
  CA: "5062345678",
  CC: "412345678",
  CD: "991234567",
  CF: "70012345",
  CG: "061234567",
  CH: "781234567",
  CI: "0123456789",
  CK: "71234",
  CL: "221234567",
  CM: "671234567",
  CN: "13123456789",
  CO: "3211234567",
  CR: "83123456",
  CU: "51234567",
  CV: "9911234",
  CW: "95181234",
  CX: "412345678",
  CY: "96123456",
  CZ: "601123456",
  DE: "15123456789",
  DJ: "77831001",
  DK: "34412345",
  DM: "7672251234",
  DO: "8092345678",
  DZ: "551234567",
  EC: "991234567",
  EE: "51234567",
  EG: "1001234567",
  EH: "650123456",
  ER: "7123456",
  ES: "612345678",
  ET: "911234567",
  FI: "412345678",
  FJ: "7012345",
  FK: "51234",
  FM: "3501234",
  FO: "211234",
  FR: "612345678",
  GA: "06031234",
  GB: "7400123456",
  GD: "4734031234",
  GE: "555123456",
  GF: "694201234",
  GG: "7781123456",
  GH: "231234567",
  GI: "57123456",
  GL: "221234",
  GM: "3012345",
  GN: "601123456",
  GP: "690001234",
  GQ: "222123456",
  GR: "6912345678",
  GT: "51234567",
  GU: "6713001234",
  GW: "955012345",
  GY: "6091234",
  HK: "51234567",
  HN: "91234567",
  HR: "921234567",
  HT: "34101234",
  HU: "201234567",
  ID: "812345678",
  IE: "850123456",
  IL: "502345678",
  IM: "7924123456",
  IN: "8123456789",
  IO: "3801234",
  IQ: "7912345678",
  IR: "9123456789",
  IS: "6111234",
  IT: "3123456789",
  JE: "7797712345",
  JM: "8762101234",
  JO: "790123456",
  JP: "9012345678",
  KE: "712123456",
  KG: "700123456",
  KH: "91234567",
  KI: "72001234",
  KM: "3212345",
  KN: "8697652917",
  KP: "1921234567",
  KR: "1020000000",
  KW: "50012345",
  KY: "3453231234",
  KZ: "7710009998",
  LA: "2023123456",
  LB: "71123456",
  LC: "7582845678",
  LI: "660234567",
  LK: "712345678",
  LR: "770123456",
  LS: "50123456",
  LT: "61234567",
  LU: "628123456",
  LV: "21234567",
  LY: "912345678",
  MA: "650123456",
  MC: "612345678",
  MD: "62112345",
  ME: "60123456",
  MF: "690001234",
  MG: "321234567",
  MH: "2351234",
  MK: "72345678",
  ML: "65012345",
  MM: "92123456",
  MN: "88123456",
  MO: "66123456",
  MP: "6702345678",
  MQ: "696201234",
  MR: "22123456",
  MS: "6644923456",
  MT: "96961234",
  MU: "52512345",
  MV: "7712345",
  MW: "991234567",
  MX: "2221234567",
  MY: "123456789",
  MZ: "821234567",
  NA: "811234567",
  NC: "751234",
  NE: "93123456",
  NF: "381234",
  NG: "8021234567",
  NI: "81234567",
  NL: "612345678",
  NO: "40612345",
  NP: "9841234567",
  NR: "5551234",
  NU: "8884012",
  NZ: "211234567",
  OM: "92123456",
  PA: "61234567",
  PE: "912345678",
  PF: "87123456",
  PG: "70123456",
  PH: "9051234567",
  PK: "3012345678",
  PL: "512345678",
  PM: "551234",
  PR: "7872345678",
  PS: "599123456",
  PT: "912345678",
  PW: "6201234",
  PY: "961456789",
  QA: "33123456",
  RE: "692123456",
  RO: "712034567",
  RS: "601234567",
  RU: "9123456789",
  RW: "720123456",
  SA: "512345678",
  SB: "7421234",
  SC: "2510123",
  SD: "911231234",
  SE: "701234567",
  SG: "81234567",
  SH: "51234",
  SI: "31234567",
  SJ: "41234567",
  SK: "912123456",
  SL: "25123456",
  SM: "66661212",
  SN: "701234567",
  SO: "71123456",
  SR: "7412345",
  SS: "977123456",
  ST: "9812345",
  SV: "70123456",
  SX: "7215205678",
  SY: "944567890",
  SZ: "76123456",
  TA: "8999",
  TC: "6492311234",
  TD: "63012345",
  TG: "90112345",
  TH: "812345678",
  TJ: "917123456",
  TK: "7290",
  TL: "77212345",
  TM: "66123456",
  TN: "20123456",
  TO: "7715123",
  TR: "5012345678",
  TT: "8682911234",
  TV: "901234",
  TW: "912345678",
  TZ: "621234567",
  UA: "501234567",
  UG: "712345678",
  US: "2015550123",
  UY: "94231234",
  UZ: "912345678",
  VA: "3123456789",
  VC: "7844301234",
  VE: "4121234567",
  VG: "2843001234",
  VI: "3406421234",
  VN: "912345678",
  VU: "5912345",
  WF: "821234",
  WS: "7212345",
  XK: "43201234",
  YE: "712345678",
  YT: "639012345",
  ZA: "711234567",
  ZM: "955123456",
  ZW: "712345678"
};

// Generated from the geographic region assignments in Google's libphonenumber
// country-code map, which follows the ITU-T E.164 numbering plan.
const COUNTRY_CALLING_CODE_REGIONS = {
  1: "US AG AI AS BB BM BS CA DM DO GD GU JM KN KY LC MP MS PR SX TC TT VC VG VI",
  7: "RU KZ",
  20: "EG",
  27: "ZA",
  30: "GR",
  31: "NL",
  32: "BE",
  33: "FR",
  34: "ES",
  36: "HU",
  39: "IT VA",
  40: "RO",
  41: "CH",
  43: "AT",
  44: "GB GG IM JE",
  45: "DK",
  46: "SE",
  47: "NO SJ",
  48: "PL",
  49: "DE",
  51: "PE",
  52: "MX",
  53: "CU",
  54: "AR",
  55: "BR",
  56: "CL",
  57: "CO",
  58: "VE",
  60: "MY",
  61: "AU CC CX",
  62: "ID",
  63: "PH",
  64: "NZ",
  65: "SG",
  66: "TH",
  81: "JP",
  82: "KR",
  84: "VN",
  86: "CN",
  90: "TR",
  91: "IN",
  92: "PK",
  93: "AF",
  94: "LK",
  95: "MM",
  98: "IR",
  211: "SS",
  212: "MA EH",
  213: "DZ",
  216: "TN",
  218: "LY",
  220: "GM",
  221: "SN",
  222: "MR",
  223: "ML",
  224: "GN",
  225: "CI",
  226: "BF",
  227: "NE",
  228: "TG",
  229: "BJ",
  230: "MU",
  231: "LR",
  232: "SL",
  233: "GH",
  234: "NG",
  235: "TD",
  236: "CF",
  237: "CM",
  238: "CV",
  239: "ST",
  240: "GQ",
  241: "GA",
  242: "CG",
  243: "CD",
  244: "AO",
  245: "GW",
  246: "IO",
  247: "AC",
  248: "SC",
  249: "SD",
  250: "RW",
  251: "ET",
  252: "SO",
  253: "DJ",
  254: "KE",
  255: "TZ",
  256: "UG",
  257: "BI",
  258: "MZ",
  260: "ZM",
  261: "MG",
  262: "RE YT",
  263: "ZW",
  264: "NA",
  265: "MW",
  266: "LS",
  267: "BW",
  268: "SZ",
  269: "KM",
  290: "SH TA",
  291: "ER",
  297: "AW",
  298: "FO",
  299: "GL",
  350: "GI",
  351: "PT",
  352: "LU",
  353: "IE",
  354: "IS",
  355: "AL",
  356: "MT",
  357: "CY",
  358: "FI AX",
  359: "BG",
  370: "LT",
  371: "LV",
  372: "EE",
  373: "MD",
  374: "AM",
  375: "BY",
  376: "AD",
  377: "MC",
  378: "SM",
  380: "UA",
  381: "RS",
  382: "ME",
  383: "XK",
  385: "HR",
  386: "SI",
  387: "BA",
  389: "MK",
  420: "CZ",
  421: "SK",
  423: "LI",
  500: "FK",
  501: "BZ",
  502: "GT",
  503: "SV",
  504: "HN",
  505: "NI",
  506: "CR",
  507: "PA",
  508: "PM",
  509: "HT",
  590: "GP BL MF",
  591: "BO",
  592: "GY",
  593: "EC",
  594: "GF",
  595: "PY",
  596: "MQ",
  597: "SR",
  598: "UY",
  599: "CW BQ",
  670: "TL",
  672: "NF",
  673: "BN",
  674: "NR",
  675: "PG",
  676: "TO",
  677: "SB",
  678: "VU",
  679: "FJ",
  680: "PW",
  681: "WF",
  682: "CK",
  683: "NU",
  685: "WS",
  686: "KI",
  687: "NC",
  688: "TV",
  689: "PF",
  690: "TK",
  691: "FM",
  692: "MH",
  850: "KP",
  852: "HK",
  853: "MO",
  855: "KH",
  856: "LA",
  880: "BD",
  886: "TW",
  960: "MV",
  961: "LB",
  962: "JO",
  963: "SY",
  964: "IQ",
  965: "KW",
  966: "SA",
  967: "YE",
  968: "OM",
  970: "PS",
  971: "AE",
  972: "IL",
  973: "BH",
  974: "QA",
  975: "BT",
  976: "MN",
  977: "NP",
  992: "TJ",
  993: "TM",
  994: "AZ",
  995: "GE",
  996: "KG",
  998: "UZ"
};

const COUNTRY_CALLING_CODES = Object.fromEntries(
  Object.entries(COUNTRY_CALLING_CODE_REGIONS).flatMap(([callingCode, regions]) => {
    return regions.split(" ").map((region) => [region, callingCode]);
  })
);

let phoneFormatterPromise = null;
const INQUIRY_FRAME_DURATION = 900;
const INQUIRY_FRAME_TIMER_BUFFER = 40;
const INQUIRY_SUCCESS_EXIT_DURATION = 220;
const INQUIRY_NEWSLETTER_REVEAL_DURATION = 520;
const INQUIRY_PRIVACY_RESIZE_DURATION = INQUIRY_NEWSLETTER_REVEAL_DURATION;
const INQUIRY_ERROR_REVEAL_DURATION = 420;
const INQUIRY_COUNTRY_FLAG_FADE_DURATION = 140;
const INQUIRY_COUNTRY_CODE_CARET_SPACE = 2;
const INQUIRY_COUNTRY_CODE_RESERVE_DIGITS = "0123456789";
const SHOPIFY_COUNTRY_FLAG_CDN_BASE = "https://cdn.shopify.com/static/images/flags";
const INQUIRY_CUSTOMER_PENDING_TTL = 15 * 60 * 1000;
const INQUIRY_CUSTOMER_STORAGE_PREFIX = "rr:product-inquiry:customer:";
const INQUIRY_LEGACY_NEWSLETTER_STORAGE_PREFIX = "rr:product-inquiry:newsletter:";
const INQUIRY_CUSTOMER_RETRY_DELAYS = [0, 600, 1800];
const INQUIRY_E164_PHONE_PATTERN = /^\+[1-9]\d{6,14}$/;

const getPhoneFormatter = () => {
  return globalThis.libphonenumber?.AsYouType ? globalThis.libphonenumber : null;
};

const loadPhoneFormatter = (url) => {
  const loadedFormatter = getPhoneFormatter();

  if (loadedFormatter || !url) {
    return Promise.resolve(loadedFormatter);
  }

  phoneFormatterPromise ||= new Promise((resolve) => {
    const script = document.createElement("script");
    script.src = url;
    script.async = true;
    script.dataset.rrPhoneFormatter = "true";
    script.addEventListener("load", () => resolve(getPhoneFormatter()), { once: true });
    script.addEventListener("error", () => resolve(null), { once: true });
    document.head.append(script);
  });

  return phoneFormatterPromise;
};

class RRProductInquiry extends HTMLElement {
  constructor() {
    super();
    this.abortController = null;
    this.frameTimer = null;
    this.focusTimer = null;
    this.successSwapTimer = null;
    this.successResizeFrame = null;
    this.successRevealFrame = null;
    this.phoneAutofillTimer = null;
    this.countryFlagTimer = null;
    this.countryFlagFrame = null;
    this.newsletterRevealTimer = null;
    this.newsletterRevealFrame = null;
    this.newsletterAutofillTimer = null;
    this.privacyTransitionTimer = null;
    this.privacyTransitionFrame = null;
    this.privacyTransitionActive = false;
    this.privacyTransitionResizesFrame = false;
    this.contactErrorTimer = null;
    this.contactErrorFrame = null;
    this.contactErrorState = "hidden";
    this.panel = null;
    this.productForm = null;
    this.opener = null;
    this.triggerText = null;
    this.titleText = null;
    this.morphTitle = null;
    this.titleMorphStartTransform = "";
    this.titleMorphResizeObserver = null;
    this.titleMorphSyncFrame = null;
    this.anchorHost = null;
    this.frameState = "closed";
    this.anchorAnimationFrame = null;
    this.anchorSettleFrame = null;
    this.anchorResizeObserver = null;
    this.anchorGeneration = 0;
    this.anchorCompensation = 0;
    this.anchorDesiredTop = null;
    this.anchorTransitionActive = false;
    this.form = null;
    this.successTemplate = null;
    this.lastOpener = null;
    this.countryCodeInput = null;
    this.countryCodeControl = null;
    this.countryCodeMeasure = null;
    this.countryFlag = null;
    this.countryFlagImage = null;
    this.countryFlagFallback = null;
    this.countryFlagRegion = "";
    this.countryFlagTargetRegion = "";
    this.phoneInput = null;
    this.emailInput = null;
    this.newsletterOption = null;
    this.newsletterOptIn = null;
    this.privacyCopy = null;
    this.privacyUncheckedCopy = null;
    this.privacyCheckedCopy = null;
    this.customerEndpoint = "";
    this.customerSyncPromise = null;
    this.submitButton = null;
    this.submitButtonLabelElement = null;
    this.submitButtonPendingLabelElement = null;
    this.emailTransport = null;
    this.phoneTransport = null;
    this.nameTransport = null;
    this.productTransport = null;
    this.storefrontTransport = null;
    this.detectedRegionTransport = null;
    this.timeZoneTransport = null;
    this.submissionDateTimeTransport = null;
    this.firstNameInput = null;
    this.lastNameInput = null;
    this.contactError = null;
    this.contactErrorText = null;
    this.allowSubmission = false;
    this.submissionPending = false;
    this.asyncSubmissionStarted = false;
    this.asyncSubmitBridge = null;
    this.asyncSubmitBridgeInstalled = false;
    this.submitButtonLabel = "";
    this.preferencesProcessingAllowed = false;
    this.detectedRegion = "";
    this.detectedCountryCode = "";
    this.newsletterConsentChoice = null;
  }

  connectedCallback() {
    this.abortController?.abort();
    this.abortController = new AbortController();
    const { signal } = this.abortController;
    this.titleMorphResizeObserver?.disconnect();
    this.titleMorphResizeObserver = null;
    this.clearTitleMorphSync();

    this.panel = this.querySelector("[data-inquiry-panel]");
    this.productForm = this.querySelector(":scope > product-form");
    this.opener = this.querySelector("[data-rr-inquiry-open]");
    this.triggerText = this.querySelector(".rr-product-inquiry__trigger-text");
    this.titleText = this.querySelector("[data-inquiry-title-text]");
    this.morphTitle = this.querySelector("[data-inquiry-morph-title]");
    this.anchorHost = this.closest("safe-sticky.product-info, .product-info");
    this.form = this.panel?.querySelector("[data-inquiry-contact-form]") ||
      this.panel?.querySelector("form") ||
      null;
    this.successTemplate = this.querySelector("[data-inquiry-success-template]");
    this.inquiryAnalytics?.dispose();
    this.inquiryAnalytics = new InquiryAnalytics(this);
    this.countryCodeInput = this.querySelector("[data-inquiry-country-code]");
    this.countryCodeControl = this.querySelector("[data-inquiry-country-code-control]");
    this.countryCodeMeasure = this.querySelector("[data-inquiry-country-code-measure]");
    this.countryFlag = this.querySelector("[data-inquiry-country-flag]");
    this.countryFlagImage = this.querySelector("[data-inquiry-country-flag-image]");
    this.countryFlagFallback = this.querySelector("[data-inquiry-country-flag-fallback]");
    this.phoneInput = this.querySelector("[data-inquiry-phone]");
    this.emailInput = this.querySelector("[data-inquiry-email]");
    this.newsletterOption = this.querySelector("[data-inquiry-newsletter-option]");
    this.newsletterOptIn = this.querySelector("[data-inquiry-newsletter-opt-in]");
    this.privacyCopy = this.querySelector("[data-inquiry-privacy-copy]");
    this.privacyUncheckedCopy = this.querySelector("[data-inquiry-privacy-unchecked]");
    this.privacyCheckedCopy = this.querySelector("[data-inquiry-privacy-checked]");
    this.customerEndpoint = this.dataset.customerEndpoint?.trim() ||
      this.dataset.newsletterEndpoint?.trim() ||
      "";
    this.submitButton = this.querySelector("[data-inquiry-submit]");
    this.submitButtonLabelElement = this.submitButton?.querySelector("[data-inquiry-submit-label]") || null;
    this.submitButtonPendingLabelElement = this.submitButton?.querySelector("[data-inquiry-submit-pending-label]") || null;
    this.emailTransport = this.querySelector("[data-inquiry-email-transport]");
    this.phoneTransport = this.querySelector("[data-inquiry-phone-transport]");
    this.nameTransport = this.querySelector("[data-inquiry-name]");
    this.productTransport = this.querySelector("[data-inquiry-product]");
    this.storefrontTransport = this.querySelector("[data-inquiry-storefront]");
    this.detectedRegionTransport = this.querySelector("[data-inquiry-detected-region]");
    this.timeZoneTransport = this.querySelector("[data-inquiry-time-zone]");
    this.submissionDateTimeTransport = this.querySelector("[data-inquiry-submission-date-time]");
    this.firstNameInput = this.querySelector("[data-inquiry-first-name]");
    this.lastNameInput = this.querySelector("[data-inquiry-last-name]");
    this.contactError = this.querySelector("[data-inquiry-contact-error]");
    this.contactErrorText = this.querySelector("[data-inquiry-contact-error-text]");
    this.contactErrorState = this.contactError && !this.contactError.hidden
      ? "visible"
      : "hidden";
    this.submissionPending = false;
    this.asyncSubmissionStarted = false;
    this.submitButtonLabel = this.submitButtonLabelElement?.textContent.trim() ||
      this.submitButton?.textContent.trim() ||
      "Submit Inquiry";

    if (this.form) {
      this.form.noValidate = true;
      this.form.setAttribute("novalidate", "novalidate");
    }

    this.installAsyncSubmissionBridge();

    if (document.fonts?.ready) {
      document.fonts.ready.then(() => {
        if (!signal.aborted && this.isConnected) {
          this.syncCountryCodeWidth();
          this.syncSubmitButtonLabelWidths();
          this.scheduleOpenTitleMorphSync();
        }
      });
    }

    if (!this.panel || !this.opener) {
      return;
    }

    if ("ResizeObserver" in window) {
      this.titleMorphResizeObserver = new ResizeObserver(() => {
        this.scheduleOpenTitleMorphSync();
      });
      this.titleMorphResizeObserver.observe(this);
      if (this.titleText) this.titleMorphResizeObserver.observe(this.titleText);
    } else if (typeof window.addEventListener === "function") {
      window.addEventListener("resize", () => this.scheduleOpenTitleMorphSync(), { signal });
    }

    this.panel.setAttribute("inert", "");
    this.productForm?.removeAttribute("inert");
    this.productForm?.setAttribute("aria-hidden", "false");
    this.opener.addEventListener("click", () => this.open(this.opener), { signal });

    this.querySelectorAll("[data-inquiry-close]").forEach((button) => {
      button.addEventListener("click", () => this.close(), { signal });
    });

    this.countryCodeInput?.addEventListener("focus", () => {
      this.countryCodeControl?.classList.add("is-editing");
      this.syncCountryCodeWidth();
    }, { signal });

    this.countryCodeInput?.addEventListener("input", () => {
      this.normalizeCountryCode(false);
      this.refreshPhonePresentation();
      this.refreshVisibleContactError();
    }, { signal });

    this.countryCodeInput?.addEventListener("blur", () => {
      this.countryCodeControl?.classList.remove("is-editing");
      this.normalizeCountryCode(true);
      this.refreshPhonePresentation();
      this.refreshVisibleContactError();
    }, { signal });

    this.countryCodeInput?.addEventListener("change", () => {
      this.normalizeCountryCode(true);
      this.refreshPhonePresentation();
      this.refreshVisibleContactError();
    }, { signal });

    this.phoneInput?.addEventListener("input", () => {
      this.reconcilePhoneAutofill();
      this.refreshVisibleContactError();
    }, { signal });

    this.phoneInput?.addEventListener("change", () => {
      this.reconcilePhoneAutofill();
      this.refreshVisibleContactError();
    }, { signal });

    const refreshEmailState = () => {
      const errorWillHide = this.contactErrorIsVisible && !this.getContactValidationError();
      this.syncNewsletterOption({ anticipatedErrorHidden: errorWillHide });
      this.refreshVisibleContactError();
    };
    this.emailInput?.addEventListener("input", refreshEmailState, { signal });
    this.emailInput?.addEventListener("change", refreshEmailState, { signal });
    this.emailInput?.addEventListener("blur", refreshEmailState, { signal });
    this.newsletterOptIn?.addEventListener("change", () => {
      this.newsletterConsentChoice = this.newsletterOptIn.checked;
      this.syncPrivacyCopy({ animate: true });
    }, { signal });
    this.countryFlagImage?.addEventListener("error", () => this.handleCountryFlagImageError(), { signal });
    [this.firstNameInput, this.lastNameInput].forEach((nameInput) => {
      nameInput?.addEventListener("input", () => this.refreshVisibleContactError(), { signal });
      nameInput?.addEventListener("change", () => this.refreshVisibleContactError(), { signal });
    });
    this.submitButton?.addEventListener("click", () => this.submitInquiry(), { signal });
    this.form?.addEventListener("submit", (event) => this.handleSubmission(event), { signal });
    this.form?.addEventListener("formdata", (event) => {
      this.querySelectorAll("[data-inquiry-autofill-field]").forEach((field) => {
        if (field.name) {
          event.formData.delete(field.name);
        }
      });
    }, { signal });

    this.initializeCountryCode();
    this.initializePrivacyContext(signal);
    this.syncNewsletterOption({ animate: false });
    this.newsletterAutofillTimer = window.setTimeout(() => {
      this.newsletterAutofillTimer = null;
      if (this.isConnected) this.syncNewsletterOption();
    }, 250);

    const returnState = this.getReturnSubmissionState();
    const pendingCustomer = this.readPendingCustomerSync();
    const returnedSuccess = Boolean(this.querySelector("[data-inquiry-success]")) ||
      returnState.contactPosted;

    if (returnedSuccess) {
      if (this.form?.querySelector("[data-inquiry-success]") &&
          !this.form.querySelector("[data-inquiry-server-errors]")) this.inquiryAnalytics.complete();
      this.ensureReturnedSuccessContent();
      this.open(null, { alignment: "bottom", returnedSuccess: true });
      if (pendingCustomer) void this.completePendingCustomerSync(pendingCustomer);
    } else if (this.querySelector("[data-inquiry-server-errors]")) {
      this.clearPendingCustomerSync();
      this.open(null);
    }
  }

  disconnectedCallback() {
    this.inquiryAnalytics?.dispose();
    this.abortController?.abort();
    this.titleMorphResizeObserver?.disconnect();
    this.titleMorphResizeObserver = null;
    this.clearTitleMorphSync();
    this.requestAnchorCancel();
    this.cancelFallbackAnchorTransition();
    this.clearFrameTimers();
    this.clearSuccessTransition();
    this.clearContactErrorAnimation();
    if (this.phoneAutofillTimer) {
      window.clearTimeout(this.phoneAutofillTimer);
      this.phoneAutofillTimer = null;
    }
    this.clearCountryFlagTransition();
    if (this.newsletterRevealTimer) {
      window.clearTimeout(this.newsletterRevealTimer);
      this.newsletterRevealTimer = null;
    }
    if (this.newsletterRevealFrame !== null) {
      window.cancelAnimationFrame(this.newsletterRevealFrame);
      this.newsletterRevealFrame = null;
    }
    if (this.newsletterAutofillTimer) {
      window.clearTimeout(this.newsletterAutofillTimer);
      this.newsletterAutofillTimer = null;
    }
    this.classList.remove(
      "is-opening",
      "is-open",
      "is-closing",
      "is-returned-success",
      "is-newsletter-transition",
      "is-newsletter-measuring",
      "is-privacy-transition"
    );
    this.resetTitleMorph();
    this.style.removeProperty("height");
    this.style.removeProperty("width");
    this.frameState = "closed";
    this.countryCodeControl?.classList.remove("is-editing");
    this.contactErrorState = "hidden";
    this.panel?.setAttribute("inert", "");
    this.panel?.setAttribute("aria-hidden", "true");
    if (this.panel) this.panel.hidden = true;
    this.productForm?.removeAttribute("inert");
    this.productForm?.setAttribute("aria-hidden", "false");
    this.opener?.setAttribute("aria-expanded", "false");
  }

  prepareProductScroll() {
    const product = this.closest?.('.product');
    if (!product || product.rrProportionalScroll ||
        !window.matchMedia('(min-width: 1000px)').matches ||
        product.querySelector(':scope > .product-content-below-gallery')) return null;
    const script = document.querySelector('script[src*="/rr-product-proportional.js"]');
    return script ? import(script.src) : null;
  }

  open(opener, { alignment = "top", returnedSuccess = false, titleFontReady = false } = {}) {
    if (!this.panel || this.frameState === "opening" || this.frameState === "open") {
      return;
    }

    if (!this.productScrollReady) {
      if (this.productScrollOpeningPending) return;
      const ready = this.prepareProductScroll();
      if (ready) {
        this.productScrollOpeningPending = true;
        ready.catch(() => {}).then(() => {
          this.productScrollOpeningPending = false;
          this.productScrollReady = true;
          if (this.isConnected) this.open(opener, { alignment, returnedSuccess, titleFontReady });
        });
        return;
      }
    }

    // A font swap changes the moving title's measured width mid-morph.
    if (!titleFontReady && document.fonts && this.morphTitle) {
      const style = getComputedStyle(this.morphTitle);
      const font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
      const sample = this.morphTitle.textContent || this.triggerText?.textContent || "Inquiry";
      if (!document.fonts.check(font, sample)) {
        if (this.titleFontOpeningPending) return;
        this.titleFontOpeningPending = true;
        document.fonts.load(font, sample).catch(() => {}).then(() => {
          this.titleFontOpeningPending = false;
          if (this.isConnected) this.open(opener, { alignment, returnedSuccess, titleFontReady: true });
        });
        return;
      }
    }

    this.lastOpener = opener || this.opener;
    if (returnedSuccess) {
      this.classList.add("is-returned-success");
    } else {
      this.classList.remove("is-returned-success");
    }
    this.initializeCountryCode();
    this.ensurePhoneFormatter();
    let activated = false;
    const activate = (coordinated = false) => {
      if (activated) return;
      activated = true;
      return this.expandFrame(coordinated);
    };
    const anchorRequest = new CustomEvent("rr:product-anchor-open", {
      bubbles: true,
      cancelable: true,
      detail: {
        anchor: this,
        alignment,
        proportionalOpen: true,
        activate,
        onComplete: () => this.focusOpenContent()
      }
    });
    const handledByProductDetails = !this.dispatchEvent(anchorRequest);

    if (!handledByProductDetails) {
      this.runFallbackAnchorTransition(activate, () => this.focusOpenContent(), alignment);
    }
  }

  clearFrameTimers() {
    this.panel?.style.removeProperty("width");
    if (this.frameTimer) {
      window.clearTimeout(this.frameTimer);
      this.frameTimer = null;
    }

    if (this.focusTimer) {
      window.clearTimeout(this.focusTimer);
      this.focusTimer = null;
    }

    this.clearTitleMorphSync();
    this.clearPrivacyTransition();
  }

  clearSuccessTransition() {
    if (this.successSwapTimer) {
      window.clearTimeout(this.successSwapTimer);
      this.successSwapTimer = null;
    }

    if (this.successResizeFrame !== null) {
      window.cancelAnimationFrame(this.successResizeFrame);
      this.successResizeFrame = null;
    }

    if (this.successRevealFrame !== null) {
      window.cancelAnimationFrame(this.successRevealFrame);
      this.successRevealFrame = null;
    }

    this.classList.remove("is-success-transition", "is-success-measuring");
    this.form?.classList.remove("is-success-leaving", "is-success-entering");
  }

  setNewsletterOptionState(visible, checked = false) {
    if (!this.newsletterOption) return;

    if (visible) {
      this.newsletterOption.classList.add("is-visible");
      this.newsletterOption.setAttribute("aria-hidden", "false");
      if (this.newsletterOptIn) {
        this.newsletterOptIn.checked = Boolean(checked);
        this.newsletterOptIn.disabled = false;
      }
    } else {
      this.newsletterOption.classList.remove("is-visible");
      this.newsletterOption.setAttribute("aria-hidden", "true");
      if (this.newsletterOptIn) {
        this.newsletterOptIn.checked = false;
        this.newsletterOptIn.disabled = true;
      }
    }
  }

  applyNewsletterOptionState(visible, checked = false) {
    this.setNewsletterOptionState(visible, checked);
    this.syncPrivacyCopy({ animate: false, preserveFrameHeight: true });
  }

  clearPrivacyTransition({ restoreHeight = true, restoreCopyHeight = true } = {}) {
    const transitionWasActive = Boolean(
      this.privacyTransitionTimer ||
      this.privacyTransitionFrame !== null ||
      this.privacyTransitionActive
    );
    const transitionResizedFrame = this.privacyTransitionResizesFrame;

    if (this.privacyTransitionTimer) {
      window.clearTimeout(this.privacyTransitionTimer);
      this.privacyTransitionTimer = null;
    }

    if (this.privacyTransitionFrame !== null) {
      window.cancelAnimationFrame(this.privacyTransitionFrame);
      this.privacyTransitionFrame = null;
    }

    this.classList.remove("is-privacy-transition", "is-privacy-measuring");
    this.privacyCopy?.classList.remove("is-transitioning");
    this.privacyTransitionActive = false;
    this.privacyTransitionResizesFrame = false;

    if (
      restoreHeight &&
      transitionWasActive &&
      transitionResizedFrame &&
      this.frameState === "open"
    ) {
      this.style.height = "auto";
    }

    if (restoreCopyHeight) {
      this.privacyCopy?.style?.removeProperty("height");
    }
  }

  applyPrivacyState(state) {
    if (!this.privacyCopy) return;

    const checked = state === "checked";
    this.privacyCopy.dataset.privacyState = checked ? "checked" : "unchecked";
    this.privacyUncheckedCopy?.setAttribute("aria-hidden", checked ? "true" : "false");
    this.privacyCheckedCopy?.setAttribute("aria-hidden", checked ? "false" : "true");
  }

  syncPrivacyCopy({
    animate = false,
    preserveFrameHeight = false,
    resizeFrame = true
  } = {}) {
    if (!this.privacyCopy || !this.privacyUncheckedCopy || !this.privacyCheckedCopy) return;

    const subscribed = Boolean(
      this.newsletterOptIn?.checked &&
      !this.newsletterOptIn.disabled
    );
    const targetState = subscribed ? "checked" : "unchecked";
    const currentState = this.privacyCopy.dataset.privacyState || "unchecked";

    const transitionWasActive = Boolean(
      this.privacyTransitionTimer ||
      this.privacyTransitionFrame !== null ||
      this.privacyTransitionActive
    );
    const interruptedHeight = transitionWasActive && this.frameState === "open"
      ? Math.ceil(this.getBoundingClientRect().height)
      : null;
    const interruptedCopyHeight = transitionWasActive && this.frameState === "open"
      ? Math.ceil(this.privacyCopy.getBoundingClientRect().height)
      : null;

    this.clearPrivacyTransition({
      restoreHeight: !preserveFrameHeight && !transitionWasActive,
      restoreCopyHeight: !transitionWasActive
    });
    if (currentState === targetState && !transitionWasActive) return;

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!animate || reduceMotion || this.frameState !== "open" || !this.panel) {
      this.applyPrivacyState(targetState);
      this.privacyCopy.style.removeProperty("height");
      if (resizeFrame && !preserveFrameHeight && this.frameState === "open") {
        this.style.height = "auto";
      }
      return;
    }

    const startHeight = interruptedHeight || Math.ceil(this.getBoundingClientRect().height);
    const startCopyHeight = interruptedCopyHeight ||
      Math.ceil(this.privacyCopy.getBoundingClientRect().height);
    if (resizeFrame) {
      this.style.height = `${startHeight}px`;
      this.style.width = "100%";
    }
    this.privacyCopy.style.height = `${startCopyHeight}px`;
    this.privacyCopy.classList.add("is-transitioning");
    this.privacyTransitionActive = true;
    this.privacyTransitionResizesFrame = resizeFrame;
    this.classList.add("is-privacy-measuring");
    if (resizeFrame) this.classList.add("is-privacy-transition");

    const targetCopy = targetState === "checked"
      ? this.privacyCheckedCopy
      : this.privacyUncheckedCopy;
    const targetCopyHeight = Math.ceil(targetCopy.getBoundingClientRect().height);
    const targetHeight = Math.max(
      0,
      Math.ceil(startHeight - startCopyHeight + targetCopyHeight)
    );
    this.getBoundingClientRect();
    this.classList.remove("is-privacy-measuring");

    this.privacyTransitionFrame = window.requestAnimationFrame(() => {
      this.privacyTransitionFrame = null;
      if (!this.isConnected || this.frameState !== "open") return;
      this.applyPrivacyState(targetState);
      if (resizeFrame) this.style.height = `${targetHeight}px`;
      this.privacyCopy.style.height = `${targetCopyHeight}px`;
    });

    this.privacyTransitionTimer = window.setTimeout(() => {
      this.privacyTransitionTimer = null;
      this.privacyTransitionActive = false;
      this.privacyTransitionResizesFrame = false;
      this.classList.remove("is-privacy-transition", "is-privacy-measuring");
      this.privacyCopy?.classList.remove("is-transitioning");
      this.privacyCopy?.style?.removeProperty("height");

      if (resizeFrame && this.frameState === "open") {
        this.style.height = "auto";
        this.announceAnchorLayoutSettled();
      }
    }, INQUIRY_PRIVACY_RESIZE_DURATION + INQUIRY_FRAME_TIMER_BUFFER);
  }

  syncNewsletterOption({ animate = true, anticipatedErrorHidden = false } = {}) {
    if (!this.newsletterOption || !this.newsletterOptIn) return;

    const email = this.emailInput?.value.trim() || "";
    const visible = Boolean(
      this.emailLooksComplete(email) &&
      this.customerEndpointUrl
    );
    const currentlyVisible = this.newsletterOption.classList.contains("is-visible");
    const currentChecked = Boolean(this.newsletterOptIn.checked);
    const targetChecked = visible
      ? this.newsletterConsentChoice ?? this.detectedCountryCode === "US"
      : false;

    if (this.newsletterRevealFrame !== null) {
      window.cancelAnimationFrame(this.newsletterRevealFrame);
      this.newsletterRevealFrame = null;
    }

    if (visible === currentlyVisible) {
      const consentChanged = visible && currentChecked !== targetChecked;
      this.setNewsletterOptionState(visible, targetChecked);
      this.syncPrivacyCopy({
        animate: consentChanged && animate,
        preserveFrameHeight: !consentChanged
      });
      return;
    }

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!animate || reduceMotion || this.frameState !== "open" || !this.panel) {
      this.applyNewsletterOptionState(visible, targetChecked);
      return;
    }

    if (this.newsletterRevealTimer) {
      window.clearTimeout(this.newsletterRevealTimer);
      this.newsletterRevealTimer = null;
    }

    const startHeight = Math.ceil(this.getBoundingClientRect().height);
    this.style.height = `${startHeight}px`;
    this.style.width = "100%";
    this.classList.add("is-newsletter-transition", "is-newsletter-measuring");

    this.applyNewsletterOptionState(visible, targetChecked);
    let targetHeight = Math.ceil(this.panel.scrollHeight + this.frameBorderHeight);

    if (anticipatedErrorHidden && this.contactErrorIsVisible) {
      const errorStyles = window.getComputedStyle(this.contactError);
      const errorContribution = this.contactError.getBoundingClientRect().height +
        (Number.parseFloat(errorStyles.marginBlockStart) || 0) +
        (Number.parseFloat(errorStyles.marginBlockEnd) || 0);
      targetHeight = Math.max(0, Math.ceil(targetHeight - errorContribution));
    }
    this.applyNewsletterOptionState(currentlyVisible, currentChecked);
    this.getBoundingClientRect();
    this.classList.remove("is-newsletter-measuring");

    this.newsletterRevealFrame = requestAnimationFrame(() => {
      this.newsletterRevealFrame = null;
      if (!this.isConnected || this.frameState !== "open") return;

      this.setNewsletterOptionState(visible, targetChecked);
      if (currentChecked !== targetChecked) {
        this.syncPrivacyCopy({
          animate: true,
          preserveFrameHeight: true,
          resizeFrame: false
        });
      } else {
        this.syncPrivacyCopy({ animate: false, preserveFrameHeight: true });
      }
      this.style.height = `${targetHeight}px`;
    });

    this.newsletterRevealTimer = window.setTimeout(() => {
      this.newsletterRevealTimer = null;
      this.classList.remove("is-newsletter-transition");

      if (this.frameState === "open") {
        this.style.height = "auto";
        this.announceAnchorLayoutSettled();
      }
    }, INQUIRY_NEWSLETTER_REVEAL_DURATION + INQUIRY_FRAME_TIMER_BUFFER);
  }

  get frameBorderHeight() {
    const styles = window.getComputedStyle(this);
    return (Number.parseFloat(styles.borderTopWidth) || 0) +
      (Number.parseFloat(styles.borderBottomWidth) || 0);
  }

  get frameBorderWidth() {
    const styles = window.getComputedStyle(this);
    return (Number.parseFloat(styles.borderLeftWidth) || 0) +
      (Number.parseFloat(styles.borderRightWidth) || 0);
  }

  get collapsedFrameHeight() {
    return Math.ceil((this.opener?.getBoundingClientRect().height || 0) + this.frameBorderHeight);
  }

  get collapsedFrameWidth() {
    return Math.ceil((this.opener?.getBoundingClientRect().width || 0) + this.frameBorderWidth);
  }

  get expandedFrameWidth() {
    const owningBlock = this.closest('[data-block-type="buy-buttons"]');
    const availableWidth = owningBlock?.getBoundingClientRect().width ||
      this.parentElement?.getBoundingClientRect().width ||
      this.getBoundingClientRect().width;

    return availableWidth;
  }

  prepareTitleMorph(
    startRootRect,
    startLabelRect,
    targetRootRect,
    { atTarget = false } = {}
  ) {
    if (!this.morphTitle || !this.titleText || !startLabelRect) return;

    const targetTitleRect = this.titleText.getBoundingClientRect();
    if (!startLabelRect.width || !targetTitleRect.width) return;

    const styles = window.getComputedStyle(this);
    const borderLeft = Number.parseFloat(styles.borderLeftWidth) || 0;
    const borderTop = Number.parseFloat(styles.borderTopWidth) || 0;
    const startLeft = startLabelRect.left - startRootRect.left - borderLeft;
    const startTop = startLabelRect.top - startRootRect.top - borderTop;
    const targetLeft = targetTitleRect.left - targetRootRect.left - borderLeft;
    const targetTop = targetTitleRect.top - targetRootRect.top - borderTop;
    const translateX = window.matchMedia("(max-width: 999px)").matches ? 0 : startLeft - targetLeft;
    const translateY = startTop - targetTop;
    const scale = startLabelRect.width / targetTitleRect.width;

    this.titleMorphStartTransform = `translate3d(${translateX}px, ${translateY}px, 0) scale(${scale})`;
    this.morphTitle.classList.add("is-preparing");
    this.morphTitle.style.left = `${targetLeft}px`;
    this.morphTitle.style.top = `${targetTop}px`;
    this.morphTitle.style.width = `${targetTitleRect.width}px`;
    this.morphTitle.style.transform = atTarget ? "none" : this.titleMorphStartTransform;
    this.morphTitle.classList.add("is-active");
  }

  clearTitleMorphSync() {
    if (this.titleMorphSyncFrame === null) return;

    window.cancelAnimationFrame(this.titleMorphSyncFrame);
    this.titleMorphSyncFrame = null;
  }

  scheduleOpenTitleMorphSync() {
    if (
      this.frameState !== "open" ||
      !this.morphTitle?.classList.contains("is-active") ||
      this.titleMorphSyncFrame !== null
    ) {
      return;
    }

    this.titleMorphSyncFrame = window.requestAnimationFrame(() => {
      this.titleMorphSyncFrame = null;
      this.syncOpenTitleMorphGeometry();
    });
  }

  syncOpenTitleMorphGeometry() {
    this.clearTitleMorphSync();
    if (
      this.frameState !== "open" ||
      !this.morphTitle?.classList.contains("is-active") ||
      !this.triggerText ||
      !this.titleText
    ) {
      return;
    }

    const rootRect = this.getBoundingClientRect();
    const startLabelRect = this.triggerText.getBoundingClientRect();
    this.prepareTitleMorph(rootRect, startLabelRect, rootRect, { atTarget: true });
    this.morphTitle.getBoundingClientRect?.();
    this.morphTitle.classList.remove("is-preparing");
  }

  resetTitleMorph() {
    this.clearTitleMorphSync();
    this.titleMorphStartTransform = "";
    if (!this.morphTitle) return;

    this.morphTitle.classList.add("is-preparing");
    this.morphTitle.classList.remove("is-active");
    this.morphTitle.style.left = "";
    this.morphTitle.style.top = "";
    this.morphTitle.style.width = "";
    this.morphTitle.style.transform = "";
    this.morphTitle.getBoundingClientRect?.();
    this.morphTitle.classList.remove("is-preparing");
  }

  expandFrame(coordinated = false) {
    if (!this.panel) return;

    this.clearFrameTimers();
    this.frameState = "opening";
    const startRect = this.getBoundingClientRect();
    const startHeight = startRect.height;
    const startWidth = startRect.width;
    const targetWidth = this.expandedFrameWidth;
    const startLabelRect = this.triggerText?.getBoundingClientRect() || null;

    this.panel.hidden = false;
    this.syncSubmitButtonLabelWidths();
    this.panel.setAttribute("inert", "");
    this.panel.setAttribute("aria-hidden", "true");
    this.productForm?.setAttribute("inert", "");
    this.productForm?.setAttribute("aria-hidden", "true");
    this.panel.scrollTop = 0;
    this.opener?.setAttribute("aria-expanded", "true");
    this.style.height = `${startHeight}px`;
    this.style.width = `${startWidth}px`;
    this.getBoundingClientRect();
    this.classList.remove("is-open", "is-closing");
    this.classList.add("is-opening");

    this.classList.add("is-measuring");
    this.style.width = `${targetWidth}px`;
    this.panel.style.width = `${targetWidth - this.frameBorderWidth}px`;
    const targetHeight = Math.ceil(this.panel.scrollHeight + this.frameBorderHeight);
    const targetRootRect = this.getBoundingClientRect();
    this.prepareTitleMorph(startRect, startLabelRect, targetRootRect);
    this.style.width = `${startWidth}px`;
    this.getBoundingClientRect();
    this.morphTitle?.classList.remove("is-preparing");
    this.classList.remove("is-measuring");

    if (coordinated) {
      return {
        height: targetHeight,
        width: targetWidth,
        start: () => {
          if (this.titleMorphStartTransform) this.morphTitle.style.transform = "none";
        },
        finish: () => this.finishFrameOpen()
      };
    }

    requestAnimationFrame(() => {
      if (this.frameState === "opening") {
        this.style.width = `${targetWidth}px`;
        this.style.height = `${targetHeight}px`;
        if (this.titleMorphStartTransform) {
          this.morphTitle.style.transform = "none";
        }
      }
    });

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    this.frameTimer = window.setTimeout(
      () => this.finishFrameOpen(),
      reduceMotion ? 0 : INQUIRY_FRAME_DURATION + INQUIRY_FRAME_TIMER_BUFFER
    );
  }

  finishFrameOpen() {
    if (this.frameState !== "opening") return;

    this.frameTimer = null;
    this.frameState = "open";
    if (this.titleMorphStartTransform) {
      this.morphTitle.style.transform = "none";
    }
    this.classList.remove("is-opening");
    this.classList.add("is-open");
    this.style.width = "100%";
    this.style.height = "auto";
    this.panel?.style.removeProperty("width");
    this.scheduleOpenTitleMorphSync();
    this.panel?.removeAttribute("inert");
    this.panel?.setAttribute("aria-hidden", "false");
  }

  focusOpenContent() {
    if (
      this.frameState === "opening" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      this.finishFrameOpen();
    }

    if (this.frameState !== "open") return;

    const focusTarget = this.querySelector("[data-inquiry-success-focus]") ||
      this.firstNameInput ||
      this.querySelector("[data-inquiry-close]");
    focusTarget?.focus({ preventScroll: true });
  }

  transitionToSuccess(successContent) {
    if (!this.form || !successContent) return;

    this.requestAnchorCancel();
    let activated = false;
    const activate = () => {
      if (activated) return;
      activated = true;
      this.swapToSuccess(successContent);
    };
    const onComplete = () => this.finishSuccessTransition();
    const anchorRequest = new CustomEvent("rr:product-anchor-open", {
      bubbles: true,
      cancelable: true,
      detail: { anchor: this, alignment: "bottom", activate, onComplete }
    });
    const handledByProductDetails = !this.dispatchEvent(anchorRequest);

    if (!handledByProductDetails) {
      this.runFallbackAnchorTransition(activate, onComplete, "bottom");
    }
  }

  measureSuccessFrameHeight(successContent) {
    const fallbackHeight = Math.ceil(this.panel.scrollHeight + this.frameBorderHeight);
    const inner = this.panel?.querySelector?.(".rr-product-inquiry__inner");

    if (
      !inner ||
      typeof document.createElement !== "function" ||
      typeof successContent?.cloneNode !== "function"
    ) {
      return fallbackHeight;
    }

    const measure = document.createElement("div");
    measure.className = "rr-product-inquiry__success-measure";
    measure.setAttribute("aria-hidden", "true");
    measure.append(successContent.cloneNode(true));

    let targetHeight = fallbackHeight;
    this.classList.add("is-success-measuring");
    try {
      inner.append(measure);
      targetHeight = Math.ceil(this.panel.scrollHeight + this.frameBorderHeight);
    } finally {
      measure.remove();
      this.classList.remove("is-success-measuring");
      this.getBoundingClientRect();
    }

    return targetHeight;
  }

  swapToSuccess(successContent) {
    if (!this.form || !this.panel) return;

    this.clearSuccessTransition();
    this.clearPrivacyTransition();
    this.clearContactErrorAnimation();
    if (this.newsletterRevealTimer) {
      window.clearTimeout(this.newsletterRevealTimer);
      this.newsletterRevealTimer = null;
    }
    if (this.newsletterRevealFrame !== null) {
      window.cancelAnimationFrame(this.newsletterRevealFrame);
      this.newsletterRevealFrame = null;
    }
    this.classList.remove("is-newsletter-transition", "is-newsletter-measuring");
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const startHeight = Math.ceil(this.getBoundingClientRect().height);
    const startScrollY = window.scrollY;
    this.style.height = `${startHeight}px`;
    this.style.width = "100%";
    this.getBoundingClientRect();
    const targetHeight = this.measureSuccessFrameHeight(successContent);

    // Keep the live page geometry fixed while the shorter success state is
    // measured. Near the document's lower scroll limit, allowing that probe to
    // collapse the in-flow frame makes the browser clamp scrollY for one frame.
    if (Math.abs(window.scrollY - startScrollY) > 0.5) {
      window.scrollTo(0, startScrollY);
    }

    this.classList.add("is-success-transition");
    this.form.classList.add("is-success-leaving");
    this.resetTitleMorph();

    const resizeFrame = () => {
      this.successResizeFrame = null;
      if (!this.form || !this.panel) return;

      this.style.height = `${targetHeight}px`;
    };

    const replaceContent = () => {
      this.successSwapTimer = null;
      if (!this.form || !this.panel) return;

      this.form.classList.remove("is-success-leaving");
      this.form.classList.add("is-success-entering");
      this.form.replaceChildren(successContent);
      this.panel.scrollTop = 0;
      this.form.getBoundingClientRect?.();

      if (reduceMotion) {
        this.form.classList.remove("is-success-entering");
        return;
      }

      this.successRevealFrame = window.requestAnimationFrame(() => {
        this.successRevealFrame = null;
        this.form?.classList.remove("is-success-entering");
      });
    };

    if (reduceMotion) {
      resizeFrame();
      replaceContent();
    } else {
      this.successResizeFrame = window.requestAnimationFrame(resizeFrame);
      this.successSwapTimer = window.setTimeout(replaceContent, INQUIRY_SUCCESS_EXIT_DURATION);
    }
  }

  finishSuccessTransition() {
    this.clearSuccessTransition();
    this.style.height = "auto";
    this.style.width = "100%";
    this.setSubmissionPending(false);
    this.focusOpenContent();
  }

  runFallbackAnchorTransition(activate, onComplete, alignment = "top") {
    this.cancelFallbackAnchorTransition();

    const generation = this.anchorGeneration;
    const startY = window.scrollY;
    const startAnchorRect = this.getBoundingClientRect();
    const startAnchorTop = startAnchorRect.top;
    const useBottomEdge = alignment === "bottom";
    const startAnchorEdge = useBottomEdge ? startAnchorRect.bottom : startAnchorRect.top;
    const anchorOffset = 150;
    const getTargetViewportEdge = () => {
      return useBottomEdge ? window.innerHeight - anchorOffset : anchorOffset;
    };
    const duration = 900;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const stickyPosition = this.anchorHost
      ? window.getComputedStyle(this.anchorHost).position
      : "static";
    const compensate = Boolean(
      this.anchorHost &&
      !reduceMotion &&
      (stickyPosition === "sticky" || stickyPosition === "-webkit-sticky")
    );

    this.anchorTransitionActive = true;
    this.anchorCompensation = 0;
    this.anchorDesiredTop = compensate ? startAnchorTop : null;
    document.documentElement.classList.add("rr-details-opening");

    if (compensate) {
      this.anchorHost.classList.add("rr-product-inquiry__scroll-compensated");
      this.anchorHost.style.setProperty("--rr-product-inquiry-anchor-compensation", "0px");

      if ("ResizeObserver" in window) {
        this.anchorResizeObserver = new ResizeObserver(() => this.applyFallbackAnchorCompensation());
        this.anchorResizeObserver.observe(this.anchorHost);
      }
    }

    activate();
    this.applyFallbackAnchorCompensation();

    const clampScrollY = (targetY) => {
      const maximumY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      return Math.min(Math.max(0, targetY), maximumY);
    };
    const resolveTargetY = () => {
      const anchorRect = this.getBoundingClientRect();
      const renderedAnchorEdge = useBottomEdge ? anchorRect.bottom : anchorRect.top;
      const untransformedAnchorEdge = renderedAnchorEdge - this.anchorCompensation;
      return clampScrollY(
        window.scrollY + untransformedAnchorEdge - getTargetViewportEdge()
      );
    };

    if (reduceMotion) {
      window.scrollTo(0, resolveTargetY());
      this.finishFallbackAnchorTransition(generation, onComplete, alignment);
      return;
    }

    const startTime = performance.now();
    const easeInOutCubic = (progress) => {
      return progress < 0.5
        ? 4 * progress * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 3) / 2;
    };
    const animate = (currentTime) => {
      if (generation !== this.anchorGeneration || !this.anchorTransitionActive) return;

      const progress = Math.min((currentTime - startTime) / duration, 1);
      const easedProgress = easeInOutCubic(progress);
      const resolvedTargetY = resolveTargetY();
      const distance = resolvedTargetY - startY;

      window.scrollTo(0, startY + distance * easedProgress);
      if (compensate) {
        const desiredEdge =
          startAnchorEdge +
          (getTargetViewportEdge() - startAnchorEdge) * easedProgress;
        const currentAnchorHeight = this.getBoundingClientRect().height;
        this.anchorDesiredTop = useBottomEdge
          ? desiredEdge - currentAnchorHeight
          : desiredEdge;
      } else {
        this.anchorDesiredTop = null;
      }
      this.applyFallbackAnchorCompensation();

      if (progress < 1) {
        this.anchorAnimationFrame = window.requestAnimationFrame(animate);
      } else {
        window.scrollTo(0, resolveTargetY());
        this.anchorAnimationFrame = null;
        this.finishFallbackAnchorTransition(generation, onComplete, alignment);
      }
    };

    this.anchorAnimationFrame = window.requestAnimationFrame(animate);
  }

  applyFallbackAnchorCompensation() {
    if (!this.anchorHost || this.anchorDesiredTop === null) return;

    const renderedAnchorTop = this.getBoundingClientRect().top;
    const untransformedAnchorTop = renderedAnchorTop - this.anchorCompensation;
    this.anchorCompensation = this.anchorDesiredTop - untransformedAnchorTop;
    this.anchorHost.style.setProperty(
      "--rr-product-inquiry-anchor-compensation",
      `${this.anchorCompensation}px`
    );
  }

  finishFallbackAnchorTransition(generation, onComplete, alignment = "top") {
    if (generation !== this.anchorGeneration || !this.anchorTransitionActive) return;

    if (this.anchorDesiredTop === null) {
      this.completeFallbackAnchorTransition(generation, onComplete);
      return;
    }

    const useBottomEdge = alignment === "bottom";
    const targetViewportEdge = useBottomEdge ? window.innerHeight - 150 : 150;
    const currentAnchorHeight = this.getBoundingClientRect().height;
    this.anchorDesiredTop = useBottomEdge
      ? targetViewportEdge - currentAnchorHeight
      : targetViewportEdge;
    this.applyFallbackAnchorCompensation();
    let settleFrame = 0;

    const releaseWhenSettled = () => {
      if (generation !== this.anchorGeneration || !this.anchorTransitionActive) return;

      this.applyFallbackAnchorCompensation();

      if (Math.abs(this.anchorCompensation) > 0.75 && settleFrame < 30) {
        settleFrame += 1;
        this.anchorSettleFrame = window.requestAnimationFrame(releaseWhenSettled);
        return;
      }

      this.anchorSettleFrame = null;
      this.completeFallbackAnchorTransition(generation, onComplete);
    };

    this.anchorSettleFrame = window.requestAnimationFrame(releaseWhenSettled);
  }

  completeFallbackAnchorTransition(generation, onComplete) {
    if (generation !== this.anchorGeneration || !this.anchorTransitionActive) return;

    this.anchorGeneration += 1;
    this.clearFallbackAnchorResources();
    this.announceAnchorLayoutSettled();
    onComplete?.();
  }

  cancelFallbackAnchorTransition(announce = true) {
    if (!this.anchorTransitionActive) return;

    const wasActive = this.anchorTransitionActive;
    this.anchorGeneration += 1;
    this.clearFallbackAnchorResources();

    if (wasActive && announce) this.announceAnchorLayoutSettled();
  }

  clearFallbackAnchorResources() {
    if (this.anchorAnimationFrame !== null) {
      window.cancelAnimationFrame(this.anchorAnimationFrame);
      this.anchorAnimationFrame = null;
    }

    if (this.anchorSettleFrame !== null) {
      window.cancelAnimationFrame(this.anchorSettleFrame);
      this.anchorSettleFrame = null;
    }

    this.anchorResizeObserver?.disconnect();
    this.anchorResizeObserver = null;
    this.anchorHost?.classList.remove("rr-product-inquiry__scroll-compensated");
    this.anchorHost?.style.removeProperty("--rr-product-inquiry-anchor-compensation");
    this.anchorCompensation = 0;
    this.anchorDesiredTop = null;
    this.anchorTransitionActive = false;

    const anotherAnchorTransition = document.querySelector?.(
      ".rr-details__scroll-compensated, .rr-product-inquiry__scroll-compensated"
    );
    if (!anotherAnchorTransition) {
      document.documentElement.classList.remove("rr-details-opening");
    }
  }

  announceAnchorLayoutSettled() {
    document.dispatchEvent(new CustomEvent("rr:details-layout-settled", {
      detail: { productLayout: this.closest(".product") }
    }));
  }

  ensurePhoneFormatter() {
    void loadPhoneFormatter(this.dataset.phoneFormatterUrl || "").then((formatter) => {
      if (formatter?.AsYouType && this.isConnected) {
        this.reconcilePhoneAutofill();
        this.refreshVisibleContactError();
      }
    });
  }

  close() {
    if (
      !this.panel ||
      this.submissionPending ||
      this.frameState === "closed" ||
      this.frameState === "closing"
    ) {
      return;
    }

    this.requestAnchorCancel();
    let activated = false;
    const activate = () => {
      if (activated) return;
      activated = true;
      this.collapseFrame();
    };
    const onComplete = () => {
      if (this.frameState === "closing") this.finishFrameClose();
      (this.lastOpener || this.opener)?.focus({ preventScroll: true });
    };
    const anchorRequest = new CustomEvent("rr:product-anchor-close", {
      bubbles: true,
      cancelable: true,
      detail: { anchor: this, activate, onComplete }
    });
    const handledByProductDetails = !this.dispatchEvent(anchorRequest);

    if (!handledByProductDetails) {
      this.runFallbackAnchorTransition(activate, onComplete);
    }
  }

  collapseFrame() {
    this.clearFrameTimers();
    this.syncOpenTitleMorphGeometry();
    const startRect = this.getBoundingClientRect();
    const startHeight = Math.ceil(startRect.height);
    const startWidth = Math.ceil(startRect.width);
    const targetHeight = this.collapsedFrameHeight;
    const targetWidth = this.collapsedFrameWidth;

    this.frameState = "closing";
    this.style.height = `${startHeight}px`;
    this.style.width = `${startWidth}px`;
    this.getBoundingClientRect();
    this.classList.remove("is-opening", "is-open");
    this.classList.add("is-closing");
    this.opener?.setAttribute("aria-expanded", "false");
    this.panel?.setAttribute("inert", "");
    this.panel?.setAttribute("aria-hidden", "true");

    requestAnimationFrame(() => {
      if (this.frameState === "closing") {
        this.style.width = `${targetWidth}px`;
        this.style.height = `${targetHeight}px`;
        if (this.titleMorphStartTransform) {
          this.morphTitle.style.transform = this.titleMorphStartTransform;
        }
      }
    });

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    this.frameTimer = window.setTimeout(
      () => this.finishFrameClose(),
      reduceMotion ? 0 : INQUIRY_FRAME_DURATION + INQUIRY_FRAME_TIMER_BUFFER
    );
  }

  finishFrameClose() {
    if (this.frameState !== "closing") return;

    this.frameTimer = null;
    this.frameState = "closed";
    this.panel.hidden = true;
    this.panel.setAttribute("inert", "");
    this.panel.setAttribute("aria-hidden", "true");
    this.classList.remove("is-closing");
    this.resetTitleMorph();
    this.style.removeProperty("height");
    this.style.removeProperty("width");
    this.productForm?.removeAttribute("inert");
    this.productForm?.setAttribute("aria-hidden", "false");
  }

  requestAnchorCancel() {
    if (this.frameState === "closed" && !this.anchorTransitionActive) return;

    const dispatchTarget = this.isConnected ? this : this.anchorHost;
    dispatchTarget?.dispatchEvent(new CustomEvent("rr:product-anchor-cancel", {
      bubbles: true,
      cancelable: true,
      detail: { anchor: this }
    }));
    this.cancelFallbackAnchorTransition();
  }

  get countryCodeDigits() {
    return (this.countryCodeInput?.value || "").replace(/\D/g, "").slice(0, 3);
  }

  get phoneDigits() {
    return (this.phoneInput?.value || "").replace(/\D/g, "");
  }

  get storefrontCallingCode() {
    const storefrontCountry = (this.countryCodeInput?.dataset?.storefrontCountry || "")
      .trim()
      .toUpperCase();

    return COUNTRY_CALLING_CODES[storefrontCountry] || "1";
  }

  restoreCountryCodeAfterAutofill() {
    if (!this.countryCodeInput || this.countryCodeInput.value.trim() !== "") {
      return;
    }

    this.countryCodeInput.value = `+${this.storefrontCallingCode}`;
  }

  reconcilePhoneAutofill() {
    this.restoreCountryCodeAfterAutofill();
    this.refreshPhonePresentation();

    if (typeof window === "undefined") {
      return;
    }

    if (this.phoneAutofillTimer) {
      window.clearTimeout(this.phoneAutofillTimer);
    }

    this.phoneAutofillTimer = window.setTimeout(() => {
      this.phoneAutofillTimer = null;

      if (!this.isConnected) {
        return;
      }

      this.restoreCountryCodeAfterAutofill();
      this.refreshPhonePresentation();
    }, 50);
  }

  initializeCountryCode() {
    if (!this.countryCodeInput) {
      return;
    }

    if (this.countryCodeInput.dataset.countryCodeInitialized !== "true") {
      this.countryCodeInput.value = `+${this.storefrontCallingCode}`;
      this.countryCodeInput.dataset.countryCodeInitialized = "true";
    } else {
      this.restoreCountryCodeAfterAutofill();
    }

    this.normalizeCountryCode(true);
    this.refreshPhonePresentation({ animateFlag: false });
  }

  refreshPhonePresentation({ animateFlag = true } = {}) {
    this.syncCountryCodeWidth();
    this.formatPhone();
    this.syncCountryFlag({ animate: animateFlag });
  }

  syncCountryCodeWidth() {
    if (!this.countryCodeInput || !this.countryCodeControl || !this.countryCodeMeasure) {
      return;
    }

    const value = this.countryCodeInput.value || "+";
    this.countryCodeMeasure.textContent = value;

    if (typeof getComputedStyle !== "function") {
      return;
    }

    const inputStyles = getComputedStyle(this.countryCodeInput);
    const inputChromeWidth = [
      inputStyles.paddingLeft,
      inputStyles.paddingRight,
      inputStyles.borderLeftWidth,
      inputStyles.borderRightWidth
    ].reduce((total, measurement) => total + (Number.parseFloat(measurement) || 0), 0);
    const textWidth = this.countryCodeMeasure.getBoundingClientRect().width;
    const editingReserve = this.countryCodeControl.classList?.contains("is-editing") &&
      this.countryCodeDigits.length < 3
      ? Array.from(INQUIRY_COUNTRY_CODE_RESERVE_DIGITS).reduce((widestAdvance, digit) => {
          this.countryCodeMeasure.textContent = `${value}${digit}`;
          const nextWidth = this.countryCodeMeasure.getBoundingClientRect().width;

          return Math.max(widestAdvance, nextWidth - textWidth);
        }, 0)
      : 0;

    this.countryCodeMeasure.textContent = value;

    const controlWidth = inputChromeWidth + textWidth + editingReserve +
      INQUIRY_COUNTRY_CODE_CARET_SPACE;

    if (!Number.isFinite(controlWidth) || controlWidth <= 0) {
      return;
    }

    this.countryCodeControl.style.setProperty(
      "--rr-inquiry-country-code-width",
      `${controlWidth.toFixed(3)}px`
    );
  }

  countryFlagEmoji(countryCode) {
    const normalizedCountry = String(countryCode || "").trim().toUpperCase();

    if (!/^[A-Z]{2}$/.test(normalizedCountry)) {
      return "";
    }

    return [...normalizedCountry]
      .map((character) => String.fromCodePoint(127397 + character.charCodeAt(0)))
      .join("");
  }

  countryFlagSource(countryCode) {
    const normalizedCountry = String(countryCode || "").trim().toLowerCase();

    if (!/^[a-z]{2}$/.test(normalizedCountry)) {
      return "";
    }

    return `${SHOPIFY_COUNTRY_FLAG_CDN_BASE}/${normalizedCountry}.svg?width=40`;
  }

  handleCountryFlagImageError() {
    if (this.countryFlagImage?.dataset.countryCode !== this.countryFlagRegion) {
      return;
    }

    this.countryFlagImage.hidden = true;
    this.countryFlagImage.removeAttribute("src");
  }

  resolveCountryFlagRegion(formatter = getPhoneFormatter(), phoneDigits = this.phoneDigits) {
    const regions = (COUNTRY_CALLING_CODE_REGIONS[this.countryCodeDigits] || "")
      .split(" ")
      .filter(Boolean);

    if (regions.length === 0) {
      return "";
    }

    if (formatter?.AsYouType && phoneDigits) {
      try {
        const detector = new formatter.AsYouType();
        detector.input(`+${this.countryCodeDigits}${phoneDigits}`);
        const detectedCountry = detector.getCountry?.() || "";

        if (regions.includes(detectedCountry)) {
          return detectedCountry;
        }
      } catch (_error) {
        // Fall through to the storefront or the calling code's primary region.
      }
    }

    const storefrontCountry = (this.countryCodeInput?.dataset?.storefrontCountry || "")
      .trim()
      .toUpperCase();

    return regions.includes(storefrontCountry) ? storefrontCountry : regions[0];
  }

  clearCountryFlagTransition() {
    if (this.countryFlagTimer !== null) {
      window.clearTimeout(this.countryFlagTimer);
      this.countryFlagTimer = null;
    }

    if (this.countryFlagFrame !== null) {
      window.cancelAnimationFrame(this.countryFlagFrame);
      this.countryFlagFrame = null;
    }
  }

  applyCountryFlag(region) {
    if (!this.countryFlag || !this.countryFlagImage || !this.countryFlagFallback) {
      return;
    }

    const flagUrl = this.countryFlagSource(region);
    this.countryFlagRegion = region;
    this.countryFlagTargetRegion = region;
    this.countryFlagFallback.textContent = this.countryFlagEmoji(region);
    this.countryFlagImage.dataset.countryCode = region;

    if (flagUrl) {
      this.countryFlagImage.src = flagUrl;
      this.countryFlagImage.hidden = false;
    } else {
      this.countryFlagImage.hidden = true;
      this.countryFlagImage.removeAttribute("src");
    }

    this.countryFlag.hidden = !region;
  }

  transitionCountryFlag(region, { animate = true } = {}) {
    if (!this.countryFlag) {
      return;
    }

    const normalizedRegion = /^[A-Z]{2}$/.test(region) ? region : "";
    const transitionPending = this.countryFlagTimer !== null || this.countryFlagFrame !== null;

    if (
      normalizedRegion === this.countryFlagRegion &&
      normalizedRegion === this.countryFlagTargetRegion &&
      !transitionPending
    ) {
      return;
    }

    this.clearCountryFlagTransition();
    this.countryFlagTargetRegion = normalizedRegion;

    if (normalizedRegion === this.countryFlagRegion) {
      this.countryFlag.hidden = !normalizedRegion;
      this.countryFlag.classList.toggle("is-visible", Boolean(normalizedRegion));
      return;
    }

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const revealFlag = () => {
      this.applyCountryFlag(normalizedRegion);

      if (!normalizedRegion) {
        this.countryFlag.classList.remove("is-visible");
        return;
      }

      if (!animate || reduceMotion) {
        this.countryFlag.classList.add("is-visible");
        return;
      }

      this.countryFlag.classList.remove("is-visible");
      this.countryFlagFrame = window.requestAnimationFrame(() => {
        this.countryFlagFrame = null;
        if (this.countryFlagTargetRegion === normalizedRegion) {
          this.countryFlag.classList.add("is-visible");
        }
      });
    };

    if (!animate || reduceMotion || !this.countryFlagRegion) {
      revealFlag();
      return;
    }

    this.countryFlag.classList.remove("is-visible");
    this.countryFlagTimer = window.setTimeout(() => {
      this.countryFlagTimer = null;
      if (this.countryFlagTargetRegion === normalizedRegion) {
        revealFlag();
      }
    }, INQUIRY_COUNTRY_FLAG_FADE_DURATION);
  }

  syncCountryFlag({ animate = true } = {}) {
    if (!this.countryFlag) {
      return;
    }

    const region = this.resolveCountryFlagRegion(getPhoneFormatter(), this.phoneDigits);
    this.transitionCountryFlag(region, { animate });
  }

  normalizeCountryCode(useDefault) {
    if (!this.countryCodeInput) {
      return;
    }

    const digits = this.countryCodeDigits;
    this.countryCodeInput.value = digits
      ? `+${digits}`
      : useDefault
        ? `+${this.storefrontCallingCode}`
        : "+";
  }

  phoneValueLooksInternational() {
    const rawValue = (this.phoneInput?.value || "").trim();

    if (!rawValue) {
      return false;
    }

    if (rawValue.startsWith("+") || rawValue.startsWith("00")) {
      return true;
    }

    const digits = rawValue.replace(/\D/g, "");
    const callingCode = this.countryCodeDigits;

    if (!callingCode || !digits.startsWith(callingCode)) {
      return false;
    }

    const phoneCountry = this.resolvePhoneCountry(null);
    const exampleLength = (PHONE_EXAMPLE_NUMBERS[phoneCountry] || "").length;

    return exampleLength > 0 && digits.length > exampleLength;
  }

  normalizeAutofilledPhone(formatter = getPhoneFormatter()) {
    if (
      !this.phoneInput ||
      !this.countryCodeInput ||
      !formatter?.parsePhoneNumberFromString
    ) {
      return false;
    }

    const rawValue = this.phoneInput.value.trim();
    const digits = rawValue.replace(/\D/g, "");

    if (!rawValue || !digits) {
      return false;
    }

    let internationalCandidate = "";

    if (rawValue.startsWith("+")) {
      internationalCandidate = `+${digits}`;
    } else if (rawValue.startsWith("00")) {
      internationalCandidate = `+${digits.slice(2)}`;
    } else if (this.countryCodeDigits && digits.startsWith(this.countryCodeDigits)) {
      const phoneCountry = this.resolvePhoneCountry(formatter);
      const parsedNationalNumber = phoneCountry
        ? formatter.parsePhoneNumberFromString(digits, phoneCountry)
        : null;
      const nationalIsPossible =
        parsedNationalNumber?.isPossible?.() === true;
      const possibleInternationalCandidate = `+${digits}`;
      const internationalIsPossible =
        formatter.isPossiblePhoneNumber?.(possibleInternationalCandidate) === true;
      const parsedNationalRemovedCallingCode =
        parsedNationalNumber?.countryCallingCode === this.countryCodeDigits &&
        String(parsedNationalNumber?.nationalNumber || "") !== digits;
      const longerThanRegionalExample = this.phoneValueLooksInternational();

      if (
        internationalIsPossible &&
        (
          !nationalIsPossible ||
          parsedNationalRemovedCallingCode ||
          longerThanRegionalExample
        )
      ) {
        internationalCandidate = possibleInternationalCandidate;
      }
    }

    if (!internationalCandidate) {
      return false;
    }

    try {
      const parsedNumber = formatter.parsePhoneNumberFromString(internationalCandidate);

      if (
        !parsedNumber?.countryCallingCode ||
        !parsedNumber?.nationalNumber ||
        parsedNumber.isPossible?.() === false
      ) {
        return false;
      }

      this.countryCodeInput.value = `+${parsedNumber.countryCallingCode}`;
      this.phoneInput.value = String(parsedNumber.nationalNumber);
      this.normalizeCountryCode(false);
      return true;
    } catch (_error) {
      return false;
    }
  }

  resolvePhoneCountry(formatter, phoneDigits = "") {
    const regions = (COUNTRY_CALLING_CODE_REGIONS[this.countryCodeDigits] || "")
      .split(" ")
      .filter(Boolean);

    if (regions.length === 0) {
      return "";
    }

    const storefrontCountry = (this.countryCodeInput?.dataset?.storefrontCountry || "")
      .trim()
      .toUpperCase();

    if (regions.includes(storefrontCountry)) {
      return storefrontCountry;
    }

    if (formatter?.AsYouType && phoneDigits) {
      try {
        const detector = new formatter.AsYouType();
        detector.input(`+${this.countryCodeDigits}${phoneDigits}`);
        const detectedCountry = detector.getCountry?.() || "";

        if (regions.includes(detectedCountry)) {
          return detectedCountry;
        }
      } catch (_error) {
        // Fall through to storefront or calling-code region selection.
      }
    }

    return regions[0];
  }

  formatNationalDigits(formatter, digits, preferredCountry = "") {
    if (!formatter?.AsYouType || !this.countryCodeDigits || !digits) {
      return "";
    }

    const internationalFormatter = new formatter.AsYouType();
    const internationalNumber = internationalFormatter.input(
      `+${this.countryCodeDigits}${digits}`
    );
    const resolvedCountry = internationalFormatter.getCountry?.() || preferredCountry;

    if (this.countryCodeDigits === "1" && resolvedCountry) {
      return new formatter.AsYouType(resolvedCountry).input(digits);
    }

    const internationalPrefix = `+${this.countryCodeDigits}`;
    return internationalNumber.startsWith(internationalPrefix)
      ? internationalNumber.slice(internationalPrefix.length).trim()
      : "";
  }

  phonePlaceholder(phoneCountry, formattedExample = "", fallbackPlaceholder = "") {
    if (phoneCountry === "US") {
      return "(808) 000-0000";
    }

    const nationalStructure = formattedExample || fallbackPlaceholder;
    return nationalStructure ? nationalStructure.replace(/\d/g, "0") : "Phone number";
  }

  formatPhone() {
    if (!this.phoneInput) {
      return;
    }

    this.restoreCountryCodeAfterAutofill();

    const rawPhoneValue = this.phoneInput.value.trim();
    const explicitlyInternational =
      rawPhoneValue.startsWith("+") || rawPhoneValue.startsWith("00");
    const formatter = getPhoneFormatter();
    const normalizedAutofill = formatter
      ? this.normalizeAutofilledPhone(formatter)
      : false;
    const pattern = PHONE_PATTERNS[this.countryCodeDigits] || null;
    const rawDigits = this.phoneDigits;
    const phoneCountry = this.resolvePhoneCountry(formatter, rawDigits);
    const exampleDigits = PHONE_EXAMPLE_NUMBERS[phoneCountry] || "";
    let formattedExample = "";

    if (formatter && exampleDigits) {
      try {
        formattedExample = this.formatNationalDigits(formatter, exampleDigits, phoneCountry);
      } catch (_error) {
        formattedExample = "";
      }
    }

    this.phoneInput.placeholder = this.phonePlaceholder(
      phoneCountry,
      formattedExample,
      pattern?.placeholder
    );

    if (!formatter && rawDigits.length > 0 && this.phoneValueLooksInternational()) {
      this.ensurePhoneFormatter();
      return;
    }

    if (formatter && explicitlyInternational && !normalizedAutofill) {
      return;
    }

    if (formatter && this.countryCodeDigits && rawDigits.length > 0) {
      try {
        const formattedNationalNumber = this.formatNationalDigits(
          formatter,
          rawDigits,
          phoneCountry
        );

        if (formattedNationalNumber) {
          this.phoneInput.value = formattedNationalNumber;
          return;
        }
      } catch (_error) {
        // Preserve the lightweight fallback below if the formatter cannot parse an incomplete value.
      }
    }

    if (!pattern || rawDigits.length === 0) {
      this.phoneInput.value = rawDigits;
      return;
    }

    const maximumDigits = (pattern.mask.match(/#/g) || []).length;
    const digits = rawDigits.slice(0, maximumDigits);
    let digitIndex = 0;
    let formatted = "";

    for (const character of pattern.mask) {
      if (character === "#") {
        if (digitIndex >= digits.length) {
          break;
        }
        formatted += digits[digitIndex];
        digitIndex += 1;
      } else if ((formatted.length > 0 || character === "(") && digitIndex < digits.length) {
        formatted += character;
      }
    }

    this.phoneInput.value = formatted;
  }

  initializePrivacyContext(signal) {
    const refresh = () => this.refreshPrivacyContext();

    if (window.Shopify?.customerPrivacy) {
      refresh();
    } else {
      window.Shopify?.loadFeatures?.(
        [{ name: "consent-tracking-api", version: "0.1" }],
        (error) => {
          if (!error) {
            refresh();
          }
        }
      );
    }

    document.addEventListener("visitorConsentCollected", refresh, { signal });
  }

  refreshPrivacyContext() {
    const customerPrivacy = window.Shopify?.customerPrivacy;

    if (!customerPrivacy) {
      return;
    }

    try {
      this.preferencesProcessingAllowed = customerPrivacy.preferencesProcessingAllowed?.() === true;
      const region = customerPrivacy.getRegion?.() || "";
      this.detectedCountryCode = this.countryCodeFromRegion(region);
      this.detectedRegion = this.preferencesProcessingAllowed
        ? this.formatDetectedRegion(region)
        : "";
    } catch (_error) {
      this.preferencesProcessingAllowed = false;
      this.detectedRegion = "";
      this.detectedCountryCode = "";
    }

    this.syncNewsletterOption();
  }

  countryCodeFromRegion(region) {
    const normalizedRegion = String(region || "").trim().toUpperCase();
    return /^[A-Z]{2}/.test(normalizedRegion) ? normalizedRegion.slice(0, 2) : "";
  }

  formatDetectedRegion(region) {
    const normalizedRegion = String(region || "").trim().toUpperCase();

    if (normalizedRegion.length <= 2) {
      return normalizedRegion;
    }

    return `${normalizedRegion.slice(0, 2)} / ${normalizedRegion.slice(2)}`;
  }

  get browserTimeZone() {
    if (!this.preferencesProcessingAllowed) {
      return "";
    }

    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    } catch (_error) {
      return "";
    }
  }

  prepareNotificationFields({ customerName }) {
    this.refreshPrivacyContext();

    if (this.nameTransport) {
      this.nameTransport.value = customerName;
    }

    if (this.productTransport) {
      const context = this.productTransport.dataset;
      this.productTransport.value = [
        context.productName || "",
        context.productSku || "",
        context.productPrice || "",
        context.productUrl || ""
      ].join("\n");
    }

    if (this.storefrontTransport) {
      this.storefrontTransport.value = this.storefrontTransport.dataset.storefrontValue || "";
    }

    if (this.detectedRegionTransport) {
      this.detectedRegionTransport.value = this.detectedRegion;
    }

    if (this.timeZoneTransport) {
      this.timeZoneTransport.value = this.browserTimeZone;
    }

    if (this.submissionDateTimeTransport) {
      this.submissionDateTimeTransport.value = new Date().toISOString();
    }
  }

  clearContactErrorAnimation() {
    if (this.contactErrorTimer) {
      window.clearTimeout(this.contactErrorTimer);
      this.contactErrorTimer = null;
    }

    if (this.contactErrorFrame !== null) {
      window.cancelAnimationFrame(this.contactErrorFrame);
      this.contactErrorFrame = null;
    }
  }

  setContactErrorMessage(message) {
    const textNode = this.contactErrorText ||
      this.contactError?.querySelector?.("[data-inquiry-contact-error-text]") ||
      null;

    if (textNode) {
      textNode.textContent = message;
    } else if (this.contactError) {
      this.contactError.textContent = message;
    }
  }

  setContactFieldsInvalid(invalidFields = []) {
    const invalidSet = new Set(invalidFields.filter(Boolean));
    this.invalidContactDescriptions ||= new Map();

    new Set([this.firstNameInput, this.lastNameInput, this.phoneInput, this.emailInput,
      ...this.invalidContactDescriptions.keys(), ...invalidSet]).forEach((field) => {
      if (!field) return;

      if (invalidSet.has(field)) {
        field.setAttribute?.("aria-invalid", "true");
        if (!this.invalidContactDescriptions.has(field)) {
          this.invalidContactDescriptions.set(field, field.getAttribute?.("aria-describedby") || null);
        }
        if (this.contactError?.id) {
          const descriptions = new Set((field.getAttribute?.("aria-describedby") || "").split(/\s+/).filter(Boolean));
          descriptions.add(this.contactError.id);
          field.setAttribute?.("aria-describedby", [...descriptions].join(" "));
        }
      } else {
        field.removeAttribute?.("aria-invalid");
        if (this.invalidContactDescriptions.has(field)) {
          const description = this.invalidContactDescriptions.get(field);
          if (description) field.setAttribute?.("aria-describedby", description);
          else field.removeAttribute?.("aria-describedby");
          this.invalidContactDescriptions.delete(field);
        }
      }
    });
  }

  get contactErrorIsVisible() {
    return Boolean(
      this.contactError &&
      !this.contactError.hidden &&
      this.contactErrorState !== "hidden"
    );
  }

  clearContactError({ animate = false } = {}) {
    this.hideContactError({ animate });
    this.setContactErrorMessage("Please enter at least one contact method.");
  }

  hideContactError({ animate = true } = {}) {
    this.setContactFieldsInvalid();

    if (!this.contactError) return;

    this.clearContactErrorAnimation();
    const wasVisible = this.contactErrorIsVisible;
    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches === true;
    const canAnimate = Boolean(
      animate &&
      wasVisible &&
      !reduceMotion &&
      this.frameState === "open" &&
      typeof window.requestAnimationFrame === "function"
    );

    this.contactError.classList?.remove("is-visible");
    this.contactError.setAttribute?.("aria-hidden", "true");

    if (!canAnimate) {
      this.contactError.hidden = true;
      this.contactErrorState = "hidden";
      return;
    }

    this.contactErrorState = "hiding";
    this.contactErrorTimer = window.setTimeout(() => {
      this.contactErrorTimer = null;

      if (!this.contactError) return;
      this.contactError.hidden = true;
      this.contactErrorState = "hidden";
      this.announceAnchorLayoutSettled();
    }, INQUIRY_ERROR_REVEAL_DURATION + INQUIRY_FRAME_TIMER_BUFFER);
  }

  showContactError(message, focusTarget, invalidFields = [], { animate = true } = {}) {
    this.setContactFieldsInvalid(invalidFields);

    if (this.contactError) {
      const wasVisible = this.contactErrorIsVisible;
      this.clearContactErrorAnimation();
      this.setContactErrorMessage(message);
      this.contactError.hidden = false;
      this.contactError.setAttribute?.("aria-hidden", "false");

      const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches === true;
      const canAnimate = Boolean(
        animate &&
        !wasVisible &&
        !reduceMotion &&
        this.frameState === "open" &&
        typeof window.requestAnimationFrame === "function"
      );

      if (!canAnimate) {
        this.contactError.classList?.add("is-visible");
        this.contactErrorState = "visible";
      } else {
        this.contactError.classList?.remove("is-visible");
        this.contactErrorState = "showing";
        this.contactError.getBoundingClientRect?.();
        this.contactErrorFrame = window.requestAnimationFrame(() => {
          this.contactErrorFrame = null;

          if (!this.contactError || !this.isConnected) return;
          this.contactError.classList.add("is-visible");
          this.contactErrorState = "visible";
          this.contactErrorTimer = window.setTimeout(() => {
            this.contactErrorTimer = null;
            if (this.isConnected) this.announceAnchorLayoutSettled();
          }, INQUIRY_ERROR_REVEAL_DURATION + INQUIRY_FRAME_TIMER_BUFFER);
        });
      }
    }

    focusTarget?.focus({ preventScroll: true });
  }

  emailLooksComplete(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email);
  }

  phoneLooksComplete(phoneDigits = this.phoneDigits) {
    if (!phoneDigits || !this.countryCodeDigits) return false;

    const formatter = getPhoneFormatter();

    if (formatter?.parsePhoneNumberFromString) {
      try {
        const parsedNumber = formatter.parsePhoneNumberFromString(
          `+${this.countryCodeDigits}${phoneDigits}`
        );

        if (typeof parsedNumber?.isPossible === "function") {
          return parsedNumber.isPossible();
        }
      } catch (_error) {
        return false;
      }
    }

    const phoneCountry = this.resolvePhoneCountry(null, phoneDigits);
    const exampleLength = (PHONE_EXAMPLE_NUMBERS[phoneCountry] || "").length;

    if (exampleLength > 0) {
      return phoneDigits.length === exampleLength;
    }

    const patternLength = (PHONE_PATTERNS[this.countryCodeDigits]?.mask.match(/#/g) || []).length;
    return patternLength > 0
      ? phoneDigits.length === patternLength
      : phoneDigits.length >= 7 && phoneDigits.length <= 15;
  }

  getContactValidationError() {
    const firstName = this.firstNameInput?.value.trim() || "";
    const lastName = this.lastNameInput?.value.trim() || "";

    if (!firstName || !lastName) {
      const missingNameFields = [
        !firstName ? this.firstNameInput : null,
        !lastName ? this.lastNameInput : null
      ].filter(Boolean);

      return {
        message: "Please include your first and last name.",
        focusTarget: missingNameFields[0] || null,
        invalidFields: missingNameFields
      };
    }

    const phoneDigits = this.phoneDigits;
    const email = this.emailInput?.value.trim() || "";
    const hasPhone = this.phoneLooksComplete(phoneDigits);
    const hasEmail = email.length > 0;

    if (phoneDigits.length > 0 && !hasPhone) {
      return {
        message: "Please enter a complete phone number.",
        focusTarget: this.phoneInput,
        invalidFields: [this.phoneInput]
      };
    }

    if (hasEmail && !this.emailLooksComplete(email)) {
      return {
        message: "Please enter a complete email address.",
        focusTarget: this.emailInput,
        invalidFields: [this.emailInput]
      };
    }

    if (!hasPhone && !hasEmail) {
      return {
        message: "Please enter at least one contact method.",
        focusTarget: this.emailInput || this.phoneInput,
        invalidFields: [this.phoneInput, this.emailInput].filter(Boolean)
      };
    }

    return null;
  }

  refreshVisibleContactError() {
    if (!this.contactErrorIsVisible) return;

    const validationError = this.getContactValidationError();

    if (validationError) {
      this.showContactError(
        validationError.message,
        null,
        validationError.invalidFields,
        { animate: false }
      );
      return;
    }

    this.hideContactError();
  }

  validateAndPrepareSubmission() {
    if (!this.phoneInput || !this.emailInput || !this.emailTransport) {
      return false;
    }

    this.normalizeCountryCode(true);
    this.refreshPhonePresentation();
    const validationError = this.getContactValidationError();

    if (validationError) {
      this.showContactError(
        validationError.message,
        validationError.focusTarget,
        validationError.invalidFields
      );
      return false;
    }

    this.hideContactError();
    const phoneDigits = this.phoneDigits;
    const email = this.emailInput.value.trim();
    const hasPhone = this.phoneLooksComplete(phoneDigits);
    const hasEmail = email.length > 0;

    const fallbackEmail = this.emailTransport.dataset.fallbackEmail || this.emailTransport.value;
    this.emailTransport.value = hasEmail ? email : fallbackEmail;
    const customerPhone = hasPhone ? `+${this.countryCodeDigits || "1"} ${this.phoneInput.value.trim()}` : "";
    const customerName = `${this.firstNameInput?.value.trim() || ""} ${this.lastNameInput?.value.trim() || ""}`.trim();

    if (this.phoneTransport) {
      this.phoneTransport.value = customerPhone;
    }

    this.prepareNotificationFields({ customerName });
    this.submissionLeadId = window.crypto?.randomUUID?.() || null;
    this.persistPendingCustomerSync();
    this.inquiryAnalytics?.prepare();

    return true;
  }

  submitInquiry() {
    if (
      !this.form ||
      this.submissionPending ||
      !this.validateAndPrepareSubmission()
    ) {
      return;
    }

    this.allowSubmission = true;
    this.form.requestSubmit();
  }

  get newsletterEmail() {
    return this.emailInput?.value.trim() || "";
  }

  get newsletterPhone() {
    const phoneDigits = this.phoneDigits;
    const countryCodeDigits = this.countryCodeDigits;

    if (!countryCodeDigits || !this.phoneLooksComplete(phoneDigits)) return "";

    const phone = `+${countryCodeDigits}${phoneDigits}`;
    return INQUIRY_E164_PHONE_PATTERN.test(phone) ? phone : "";
  }

  get shouldSubscribeToNewsletter() {
    const email = this.newsletterEmail;
    return Boolean(
      email &&
      this.customerEndpointUrl &&
      this.newsletterOptIn?.checked &&
      !this.newsletterOptIn.disabled
    );
  }

  get currentInquiryCustomer() {
    const email = this.newsletterEmail;
    const firstName = this.firstNameInput?.value.trim() || "";
    const lastName = this.lastNameInput?.value.trim() || "";
    const phone = this.newsletterPhone;
    const productContext = this.productTransport?.dataset || {};
    const submittedAt = this.submissionDateTimeTransport?.value.trim() || "";
    const productName = productContext.productName?.trim() || "";
    const productSku = productContext.productSku?.trim() || "";
    const productPrice = productContext.productPrice?.trim() || "";
    const productUrl = productContext.productUrl?.trim() || "";

    if (
      !this.customerEndpointUrl ||
      !firstName ||
      !lastName ||
      (!email && !phone) ||
      !submittedAt ||
      !productName ||
      !productPrice ||
      !productUrl
    ) {
      return null;
    }

    return {
      ...(email ? { email: email.toLowerCase() } : {}),
      firstName,
      lastName,
      ...(phone ? { phone } : {}),
      marketingConsent: this.shouldSubscribeToNewsletter,
      submittedAt,
      productName,
      productSku,
      productPrice,
      productUrl,
      leadId: this.submissionLeadId,
      attribution: window.RrInquiryAttribution?.get() || null,
      test: window.Shopify?.theme?.role === "unpublished" || window.Shopify?.designMode === true,
      expiresAt: Date.now() + INQUIRY_CUSTOMER_PENDING_TTL
    };
  }

  get customerEndpointUrl() {
    if (!this.customerEndpoint) return "";

    try {
      const endpoint = new URL(this.customerEndpoint, window.location.origin);

      if (
        endpoint.origin !== window.location.origin ||
        !endpoint.pathname.startsWith("/apps/")
      ) {
        return "";
      }

      return endpoint.href;
    } catch (_error) {
      return "";
    }
  }

  get legacyNewsletterEndpointUrl() {
    const endpoint = this.customerEndpointUrl;

    if (!endpoint) return "";

    try {
      const legacyEndpoint = new URL(endpoint);
      legacyEndpoint.pathname = legacyEndpoint.pathname.replace(/\/customer\/?$/, "/newsletter");
      return legacyEndpoint.pathname.endsWith("/newsletter")
        ? legacyEndpoint.href
        : "";
    } catch (_error) {
      return "";
    }
  }

  get pendingCustomerStorageKey() {
    const inquiryId = this.panel?.id || this.opener?.getAttribute?.("aria-controls") || "";
    return inquiryId ? `${INQUIRY_CUSTOMER_STORAGE_PREFIX}${inquiryId}` : "";
  }

  get legacyPendingNewsletterStorageKey() {
    const inquiryId = this.panel?.id || this.opener?.getAttribute?.("aria-controls") || "";
    return inquiryId ? `${INQUIRY_LEGACY_NEWSLETTER_STORAGE_PREFIX}${inquiryId}` : "";
  }

  get pendingCustomerStorage() {
    try {
      return window.sessionStorage || null;
    } catch (_error) {
      return null;
    }
  }

  clearPendingCustomerSync() {
    const storage = this.pendingCustomerStorage;
    const storageKeys = [
      this.pendingCustomerStorageKey,
      this.legacyPendingNewsletterStorageKey
    ].filter(Boolean);

    if (!storage || storageKeys.length === 0) return;

    try {
      storageKeys.forEach((storageKey) => storage.removeItem(storageKey));
    } catch (_error) {
      // Storage can be unavailable in private or restricted browsing contexts.
    }
  }

  storePendingCustomerSync(pendingCustomer = this.currentInquiryCustomer || {}) {
    const {
      email = "",
      firstName = "",
      lastName = "",
      phone = "",
      marketingConsent = false,
      submittedAt = "",
      productName = "",
      productSku = "",
      productPrice = "",
      productUrl = ""
    } = pendingCustomer;
    const normalizedEmail = typeof email === "string" ? email.trim().toLowerCase() : "";
    const normalizedFirstName = typeof firstName === "string" ? firstName.trim() : "";
    const normalizedLastName = typeof lastName === "string" ? lastName.trim() : "";
    const normalizedPhone = typeof phone === "string" ? phone.trim() : "";
    const normalizedSubmittedAt = typeof submittedAt === "string" ? submittedAt.trim() : "";
    const normalizedProductName = typeof productName === "string" ? productName.trim() : "";
    const normalizedProductSku = typeof productSku === "string" ? productSku.trim() : "";
    const normalizedProductPrice = typeof productPrice === "string" ? productPrice.trim() : "";
    const normalizedProductUrl = typeof productUrl === "string" ? productUrl.trim() : "";
    const storage = this.pendingCustomerStorage;
    const storageKey = this.pendingCustomerStorageKey;
    let parsedProductUrl;

    try {
      parsedProductUrl = new URL(normalizedProductUrl);
    } catch (_error) {
      parsedProductUrl = null;
    }

    if (
      !storage ||
      !storageKey ||
      !normalizedFirstName ||
      !normalizedLastName ||
      (email !== "" && typeof email !== "string") ||
      (normalizedEmail && !this.emailLooksComplete(normalizedEmail)) ||
      (phone !== "" && typeof phone !== "string") ||
      (normalizedPhone && !INQUIRY_E164_PHONE_PATTERN.test(normalizedPhone)) ||
      (!normalizedEmail && !normalizedPhone) ||
      typeof marketingConsent !== "boolean" ||
      (marketingConsent && !normalizedEmail) ||
      !normalizedSubmittedAt ||
      !Number.isFinite(new Date(normalizedSubmittedAt).valueOf()) ||
      !normalizedProductName ||
      !normalizedProductPrice ||
      !parsedProductUrl ||
      parsedProductUrl.protocol !== "https:" ||
      parsedProductUrl.origin !== window.location.origin
    ) {
      return false;
    }

    try {
      storage.setItem(storageKey, JSON.stringify({
        ...(normalizedEmail ? { email: normalizedEmail } : {}),
        firstName: normalizedFirstName,
        lastName: normalizedLastName,
        ...(normalizedPhone ? { phone: normalizedPhone } : {}),
        marketingConsent,
        submittedAt: new Date(normalizedSubmittedAt).toISOString(),
        productName: normalizedProductName,
        productSku: normalizedProductSku,
        productPrice: normalizedProductPrice,
        productUrl: parsedProductUrl.href,
        leadId: pendingCustomer.leadId,
        attribution: pendingCustomer.attribution || null,
        test: pendingCustomer.test === true,
        expiresAt: Number.isFinite(pendingCustomer.expiresAt)
          ? pendingCustomer.expiresAt
          : Date.now() + INQUIRY_CUSTOMER_PENDING_TTL
      }));
      if (this.legacyPendingNewsletterStorageKey) {
        storage.removeItem(this.legacyPendingNewsletterStorageKey);
      }
      return true;
    } catch (_error) {
      return false;
    }
  }

  persistPendingCustomerSync() {
    const pendingCustomer = this.currentInquiryCustomer;

    if (!pendingCustomer) {
      this.clearPendingCustomerSync();
      return false;
    }

    return this.storePendingCustomerSync(pendingCustomer);
  }

  readPendingCustomerSync() {
    const storage = this.pendingCustomerStorage;
    const storageKeys = [
      this.pendingCustomerStorageKey,
      this.legacyPendingNewsletterStorageKey
    ].filter(Boolean);

    if (!storage || storageKeys.length === 0) return null;

    try {
      const storedPending = storageKeys
        .map((storageKey) => ({
          storageKey,
          serialized: storage.getItem(storageKey)
        }))
        .find(({ serialized }) => Boolean(serialized));
      const pending = JSON.parse(storedPending?.serialized || "null");
      const legacyNewsletter = storedPending?.storageKey ===
        this.legacyPendingNewsletterStorageKey;
      const normalizedEmail = typeof pending?.email === "string"
        ? pending.email.trim().toLowerCase()
        : "";
      const normalizedPhone = typeof pending?.phone === "string"
        ? pending.phone.trim()
        : "";
      const marketingConsent = pending?.marketingConsent ?? pending?.consent;
      const normalizedSubmittedAt = typeof pending?.submittedAt === "string"
        ? pending.submittedAt.trim()
        : "";
      const normalizedProductName = typeof pending?.productName === "string"
        ? pending.productName.trim()
        : "";
      const normalizedProductSku = typeof pending?.productSku === "string"
        ? pending.productSku.trim()
        : "";
      const normalizedProductPrice = typeof pending?.productPrice === "string"
        ? pending.productPrice.trim()
        : "";
      const normalizedProductUrl = typeof pending?.productUrl === "string"
        ? pending.productUrl.trim()
        : "";
      let parsedProductUrl;

      try {
        parsedProductUrl = normalizedProductUrl
          ? new URL(normalizedProductUrl)
          : null;
      } catch (_error) {
        parsedProductUrl = null;
      }

      if (
        !pending ||
        (pending.email != null && typeof pending.email !== "string") ||
        (normalizedEmail && !this.emailLooksComplete(normalizedEmail)) ||
        typeof pending.firstName !== "string" ||
        !pending.firstName.trim() ||
        typeof pending.lastName !== "string" ||
        !pending.lastName.trim() ||
        (pending.phone != null && typeof pending.phone !== "string") ||
        (normalizedPhone && !INQUIRY_E164_PHONE_PATTERN.test(normalizedPhone)) ||
        (!normalizedEmail && !normalizedPhone) ||
        typeof marketingConsent !== "boolean" ||
        (marketingConsent && !normalizedEmail) ||
        (!legacyNewsletter && (
          !normalizedSubmittedAt ||
          !Number.isFinite(new Date(normalizedSubmittedAt).valueOf()) ||
          !normalizedProductName ||
          !normalizedProductPrice ||
          !parsedProductUrl ||
          parsedProductUrl.protocol !== "https:" ||
          parsedProductUrl.origin !== window.location.origin
        )) ||
        (legacyNewsletter && marketingConsent !== true) ||
        !Number.isFinite(pending.expiresAt) ||
        pending.expiresAt <= Date.now()
      ) {
        this.clearPendingCustomerSync();
        return null;
      }

      return {
        ...(normalizedEmail ? { email: normalizedEmail } : {}),
        firstName: pending.firstName.trim(),
        lastName: pending.lastName.trim(),
        ...(normalizedPhone ? { phone: normalizedPhone } : {}),
        marketingConsent,
        leadId: pending.leadId,
        attribution: pending.attribution || null,
        test: pending.test === true,
        ...(!legacyNewsletter ? {
          submittedAt: new Date(normalizedSubmittedAt).toISOString(),
          productName: normalizedProductName,
          productSku: normalizedProductSku,
          productPrice: normalizedProductPrice,
          productUrl: parsedProductUrl.href
        } : { legacyNewsletter: true }),
        expiresAt: pending.expiresAt
      };
    } catch (_error) {
      this.clearPendingCustomerSync();
      return null;
    }
  }

  getReturnSubmissionState() {
    try {
      const url = new URL(window.location.href);
      return {
        contactPosted: url.searchParams.get("contact_posted") === "true"
      };
    } catch (_error) {
      return {
        contactPosted: false
      };
    }
  }

  ensureReturnedSuccessContent() {
    if (this.querySelector("[data-inquiry-success]")) return true;

    const successContent = this.successTemplate?.content?.firstElementChild;
    if (!this.form || !successContent) return false;

    this.form.replaceChildren(successContent.cloneNode(true));
    return true;
  }

  async syncCustomerProfile(pendingCustomer) {
    const endpoint = pendingCustomer?.legacyNewsletter
      ? this.legacyNewsletterEndpointUrl
      : this.customerEndpointUrl;

    if (!endpoint || !pendingCustomer) return false;

    try {
      const response = await window.fetch(endpoint, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          ...(pendingCustomer.email ? { email: pendingCustomer.email } : {}),
          firstName: pendingCustomer.firstName,
          lastName: pendingCustomer.lastName,
          ...(pendingCustomer.phone ? { phone: pendingCustomer.phone } : {}),
          marketingConsent: pendingCustomer.marketingConsent === true,
          ...(pendingCustomer.leadId ? { leadId: pendingCustomer.leadId } : {}),
          ...(window.RrInquiryAttribution?.allowed() && pendingCustomer.attribution ? { attribution: pendingCustomer.attribution } : {}),
          ...(pendingCustomer.test === true ? { test: true } : {}),
          ...(pendingCustomer.submittedAt ? {
            submittedAt: pendingCustomer.submittedAt,
            productName: pendingCustomer.productName,
            productSku: pendingCustomer.productSku,
            productPrice: pendingCustomer.productPrice,
            productUrl: pendingCustomer.productUrl,
            ...(pendingCustomer.inquiryType ? { inquiryType: pendingCustomer.inquiryType } : {})
          } : {})
        }),
        signal: this.abortController?.signal
      });
      const result = await response.json().catch(() => null);

      return Boolean(
        response.ok &&
        result?.ok === true &&
        result?.customerSynced === true &&
        (pendingCustomer.legacyNewsletter || result?.inquiryNoteRecorded === true) &&
        (pendingCustomer.marketingConsent !== true || result?.subscribed === true)
      );
    } catch (_error) {
      return false;
    }
  }

  completePendingCustomerSync(
    pendingCustomer = this.readPendingCustomerSync()
  ) {
    if (!pendingCustomer) return Promise.resolve(false);
    if (this.customerSyncPromise) return this.customerSyncPromise;

    const syncCustomer = async () => {
      for (const delay of INQUIRY_CUSTOMER_RETRY_DELAYS) {
        if (!this.isConnected) return false;

        if (delay > 0) {
          await new Promise((resolve) => window.setTimeout(resolve, delay));
        }

        if (await this.syncCustomerProfile(pendingCustomer)) {
          this.clearPendingCustomerSync();
          return true;
        }
      }

      return false;
    };

    this.customerSyncPromise = syncCustomer().finally(() => {
      this.customerSyncPromise = null;
    });

    return this.customerSyncPromise;
  }

  get supportsAsyncSubmission() {
    return typeof window.fetch === "function" &&
      typeof window.FormData === "function" &&
      typeof window.DOMParser === "function";
  }

  installAsyncSubmissionBridge() {
    if (
      !this.form ||
      !this.supportsAsyncSubmission ||
      this.asyncSubmitBridgeInstalled ||
      this.form.dataset.hcaptchaBound === "true"
    ) {
      return;
    }

    const asyncSubmitBridge = () => {
      void this.submitInquiryWithoutReload();
    };

    try {
      this.form.submit = asyncSubmitBridge;
      this.asyncSubmitBridge = asyncSubmitBridge;
      this.asyncSubmitBridgeInstalled = this.form.submit === asyncSubmitBridge;
    } catch (_error) {
      this.asyncSubmitBridge = null;
      this.asyncSubmitBridgeInstalled = false;
    }
  }

  setSubmissionPending(pending) {
    this.submissionPending = pending;

    if (this.form) {
      this.form.setAttribute("aria-busy", pending ? "true" : "false");
    }

    if (this.submitButton) {
      this.submitButton.disabled = pending;
      if (pending) {
        this.submitButton.setAttribute?.("aria-busy", "true");
      } else {
        this.submitButton.removeAttribute?.("aria-busy");
      }

      const label = this.submitButtonLabelElement ||
        this.submitButton.querySelector?.("[data-inquiry-submit-label]");
      const pendingLabel = this.submitButtonPendingLabelElement ||
        this.submitButton.querySelector?.("[data-inquiry-submit-pending-label]");
      if (label && pendingLabel) {
        label.setAttribute?.("aria-hidden", pending ? "true" : "false");
        pendingLabel.setAttribute?.("aria-hidden", pending ? "false" : "true");
      } else if (label) {
        label.textContent = pending ? "Submitting Inquiry" : this.submitButtonLabel;
      } else {
        this.submitButton.textContent = pending ? "Submitting Inquiry" : this.submitButtonLabel;
      }
    }
  }

  syncSubmitButtonLabelWidths() {
    const button = this.submitButton;
    const idleLabel = this.submitButtonLabelElement;
    const pendingLabel = this.submitButtonPendingLabelElement;
    if (
      !button?.style?.setProperty ||
      typeof idleLabel?.getBoundingClientRect !== "function" ||
      typeof pendingLabel?.getBoundingClientRect !== "function"
    ) {
      return;
    }

    const idleWidth = idleLabel.getBoundingClientRect().width;
    const pendingWidth = pendingLabel.getBoundingClientRect().width;
    if (idleWidth > 0) {
      button.style.setProperty("--rr-inquiry-submit-idle-label-width", `${idleWidth}px`);
    }
    if (pendingWidth > 0) {
      button.style.setProperty("--rr-inquiry-submit-pending-label-width", `${pendingWidth}px`);
    }
  }

  showSubmissionError(message) {
    this.showContactError(message, this.submitButton);
  }

  responseRequiresChallenge(response, responseDocument) {
    try {
      const responseUrl = new URL(response.url, window.location.href);
      if (/\/challenge(?:\/|$)/.test(responseUrl.pathname)) return true;
    } catch (_error) {
      // The returned document check below remains available for unusual URLs.
    }

    return Boolean(responseDocument.querySelector('form[action*="/challenge"], #shopify-challenge'));
  }

  responseReachedFormReturnTarget(response, form) {
    if (!response?.redirected || !form) return false;

    const returnTo = form
      .querySelector?.('input[name="return_to"]')
      ?.value
      ?.trim();

    if (!returnTo) return false;

    try {
      const currentUrl = new URL(window.location.href);
      const expectedUrl = new URL(returnTo, currentUrl);
      const responseUrl = new URL(response.url, currentUrl);

      return responseUrl.origin === expectedUrl.origin &&
        responseUrl.pathname === expectedUrl.pathname &&
        responseUrl.search === expectedUrl.search;
    } catch (_error) {
      return false;
    }
  }

  responseAcceptedWithoutMarker(response, form) {
    if (!response?.ok || !form) return false;

    try {
      const currentUrl = new URL(window.location.href);
      const responseUrl = new URL(response.url, currentUrl);
      const actionUrl = new URL(form.action, currentUrl);
      const returnTo = form
        .querySelector?.('input[name="return_to"]')
        ?.value
        ?.trim();

      if (responseUrl.origin !== currentUrl.origin) return false;
      if (/\/(?:challenge|password)(?:\/|$)/.test(responseUrl.pathname)) return false;
      if (responseUrl.pathname === actionUrl.pathname) return true;
      if (!returnTo) return false;

      const expectedUrl = new URL(returnTo, currentUrl);
      return responseUrl.pathname === expectedUrl.pathname;
    } catch (_error) {
      return false;
    }
  }

  async submitInquiryWithoutReload() {
    if (!this.form || this.asyncSubmissionStarted || !this.isConnected) return;

    this.asyncSubmissionStarted = true;
    this.setSubmissionPending(true);

    try {
      const response = await window.fetch(this.form.action, {
        method: (this.form.method || "post").toUpperCase(),
        body: new window.FormData(this.form),
        credentials: "same-origin",
        redirect: "follow",
        headers: { Accept: "text/html" },
        signal: this.abortController?.signal
      });
      const responseHtml = await response.text();
      const responseDocument = new window.DOMParser().parseFromString(responseHtml, "text/html");

      if (this.responseRequiresChallenge(response, responseDocument)) {
        window.location.assign(response.url);
        return;
      }

      const returnedForm = responseDocument.getElementById(this.form.id);
      const successContent = returnedForm?.querySelector("[data-inquiry-success]");
      const returnedError = returnedForm
        ?.querySelector("[data-inquiry-server-errors]")
        ?.textContent
        ?.replace(/\s+/g, " ")
        .trim();
      let contactPosted = false;

      try {
        const responseUrl = new URL(response.url, window.location.href);
        contactPosted = responseUrl.searchParams.get("contact_posted") === "true";
      } catch (_error) {
        // The rendered success marker and explicit return target remain available.
      }

      const returnedToInquiryTarget = this.responseReachedFormReturnTarget(response, this.form);
      const acceptedWithoutMarker = this.responseAcceptedWithoutMarker(response, this.form);
      const confirmedSuccess = successContent ||
        contactPosted ||
        returnedToInquiryTarget ||
        acceptedWithoutMarker;
      const resolvedSuccessContent = successContent ||
        this.successTemplate?.content?.firstElementChild ||
        null;

      if (!returnedError && response.ok && confirmedSuccess && resolvedSuccessContent) {
        if (successContent || (contactPosted && returnedToInquiryTarget)) this.inquiryAnalytics?.complete();
        const pendingCustomer = this.readPendingCustomerSync() ||
          this.currentInquiryCustomer;
        this.transitionToSuccess(resolvedSuccessContent.cloneNode(true));
        if (pendingCustomer) {
          void this.completePendingCustomerSync(pendingCustomer);
        }
        return;
      }

      const submissionError = new Error(
        returnedError || "We could not submit your inquiry. Please try again."
      );
      submissionError.name = "InquirySubmissionError";
      throw submissionError;
    } catch (error) {
      if (error?.name === "AbortError" || !this.isConnected) return;

      this.asyncSubmissionStarted = false;
      this.setSubmissionPending(false);
      this.showSubmissionError(
        error?.name === "InquirySubmissionError"
          ? error.message
          : "We could not submit your inquiry. Please check your connection and try again."
      );
    }
  }

  handleSubmission(event) {
    let prepared = false;

    if (this.allowSubmission) {
      this.allowSubmission = false;
      prepared = true;
    } else {
      prepared = this.validateAndPrepareSubmission();
    }

    if (!prepared) {
      event.preventDefault();
      return;
    }

    if (!this.supportsAsyncSubmission || !this.asyncSubmitBridgeInstalled) {
      return;
    }

    event.preventDefault();
    this.setSubmissionPending(true);

    if (this.form?.dataset.hcaptchaBound === "true") {
      return;
    }

    void this.submitInquiryWithoutReload();
  }

}

if (!customElements.get("rr-product-inquiry")) {
  customElements.define("rr-product-inquiry", RRProductInquiry);
}
